import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { useAppSelector } from '../../stores';
import { AppLayout } from '../../layouts';
import UserHeaderMenu from '../../layouts/UserHeaderMenu';
import InterviewContextTopBar from '../interview/InterviewContextTopBar';
import SidebarUserProfileSection from '../../components/SidebarUserProfileSection';
import { Dropdown } from '../../components';
import { FamlaServices, i18n } from '../../services';
import VoiceRealtimeBar from '../interview/VoiceRealtimeBar';
import { openPDFInViewer } from '../../utils';
import { getTenantInitial, getTenantName } from '../../utils/brandingStyles';
import {
    formatDate,
    formatDateTime,
    parseServerInstant,
    getChatTimestampHoverTitle,
    formatInterviewScrollHeader,
    formatCollectWorkspaceRelativeLabel,
} from '../../utils/formatDate';
import { extractDocumentText } from '../../utils/extractDocumentText';
import { renderInlineBoldMd } from '../../utils/renderInlineBoldMd';
import { contributorDisplayName } from '../../utils/contributorDisplayName';
import { dedupeRoleOptionsPreservingLabels } from '../../utils/inviteRoleOptions';
import {
    GenericModal,
    RenameDataCollectionModal,
    InviteUser,
    type InviteCustomSubmitResult,
    type InviteRoleOption,
} from '../modal';
import AnalysisFileModal from '../../swimlane/components/AnalysisPanel/components/AnalysisFileModal';
import { iMap } from '../../components/type';
import { ScrollingNotification } from '../../components/notifications/ScrollingNotification';
import { useInterviewRailMessages } from '../../hooks/useInterviewRailNotifications';
import { useInterviewThreadScroll } from '../../hooks/useInterviewThreadScroll';
import {
    addContributor,
    approveGuide,
    deleteCollectContributor,
    deleteCollectDataCollection,
    generateGuide,
    getCollectDataCollection,
    getContributorThreadForOwner,
    getGuide,
    deleteOwnerSetupMessage,
    listOwnerSetupMessages,
    listRecentCollectCollections,
    canManageDataCollectionSummary,
    patchCollectDataCollection,
    patchOwnerSetupMessage,
    remindCollectContributor,
    sendCollection,
    postOwnerSetupTurn,
    type OwnerSetupPhase,
    type OwnerSetupTurnResult,
    withdrawRecentCollectContribution,
    type RecentCollectCollectionSummary,
} from '../../services/api/collectDataService';
import { Routes } from '../../routes';
import CollectGuideReviewPanel from '../collect/CollectGuideReviewPanel';
import CollectWorkspaceSettingsModal from '../collect/CollectWorkspaceSettingsModal';
import { downloadCollectGuidePdf } from '../../utils/collectGuidePdf';
import '../../features/process/process/process-create.css';
import '../../layouts/header-redesign.css';
import '../../styles/modern-theme.css';
import '../../styles/interview-redesign.css';

type Tab = 'collect' | 'guide' | 'monitor';

function isOwnerSetupUserRole(role: string) {
    return role === 'user' || role === 'owner';
}

/** Message chat émis après upload fichier (pas cartographie seule). */
function ownerMessageIndicatesContextFile(content: string, t: TFunction): boolean {
    const c = content.trim().toLowerCase();
    if (!c) return false;
    if (c.includes("j'ai joint un fichier pour le contexte")) return true;
    if (c.includes("i've attached a file for context")) return true;
    const marker = String(t('views.processInterview.contextFileAttachedNote', { fileName: '\0' }));
    const prefix = marker.split('\0')[0]?.trim().toLowerCase();
    return Boolean(prefix && c.includes(prefix));
}

function ownerMessageIsContextOnly(content: string, t: TFunction): boolean {
    const c = content.trim().toLowerCase();
    if (!c) return true;
    if (c.includes("j'ai joint un fichier pour le contexte")) return true;
    if (c.includes("j'ai joint des cartographies")) return true;
    if (c.includes("i've attached a file for context")) return true;
    if (c.includes("i've attached maps for context")) return true;
    const skipAnswer = String(
        t('views.processInterview.skipContextAnswer', { defaultValue: 'No additional context for now.' }) ?? '',
    )
        .trim()
        .toLowerCase();
    if (skipAnswer && c.includes(skipAnswer)) return true;
    return false;
}

function countOwnerSetupUserTurns(
    messages: Array<{ role?: string; content?: string }>,
    t: TFunction,
): number {
    return messages.filter((m) => {
        if (!isOwnerSetupUserRole(String(m?.role ?? ''))) return false;
        const content = String(m?.content ?? '').trim();
        return Boolean(content) && !ownerMessageIsContextOnly(content, t);
    }).length;
}

function formatContextChecklistDetailDone(
    mapCount: number,
    hasUploadedFile: boolean,
    t: TFunction,
): string {
    const parts: string[] = [];
    if (mapCount === 1) {
        parts.push(t('form.collectWorkspace.checklist.ctx.mapsOne'));
    } else if (mapCount > 1) {
        parts.push(t('form.collectWorkspace.checklist.ctx.mapsMany', { count: mapCount }));
    }
    if (hasUploadedFile) {
        parts.push(t('form.collectWorkspace.checklist.ctx.docOne'));
    }
    if (parts.length === 0) {
        return t('form.collectWorkspace.checklist.ctx.detailDoneGeneric');
    }
    return parts.join(t('form.collectWorkspace.checklist.ctx.detailJoinSeparator'));
}

/** Popper : sortir le menu du scroll / overflow des ancêtres (sidebar collect) */
const BS_DROPDOWN_POPPER_FIXED = JSON.stringify({ strategy: 'fixed' });

/** Réponses Famla : statut métier souvent dans le JSON même si le HTTP est neutre (proxy). */
function collectContributorResponseFailed(res: { status: number; body?: { status_code?: number } }): boolean {
    const inner = res.body?.status_code;
    if (typeof inner === 'number') return inner < 200 || inner >= 300;
    const http = res.status;
    return http < 200 || http >= 300 || !(http === 200 || http === 201);
}

function responseHasDuplicateContributorRoleCode(body: unknown): boolean {
    if (!body || typeof body !== 'object') return false;
    const data = (body as { data?: unknown }).data;
    return (
        typeof data === 'object' &&
        data !== null &&
        (data as { code?: string }).code === 'duplicate_contributor_role'
    );
}

function initialsFromName(name: string) {
    const p = name.trim().split(/\s+/).filter(Boolean);
    if (!p.length) return '?';
    if (p.length === 1) return p[0].slice(0, 2).toUpperCase();
    return (p[0][0] + p[p.length - 1][0]).toUpperCase();
}

/** Libellé lisible dans le badge à côté du nom (ex. Draft, pas DRAFT) */
function collectCollectionBadgeLabel(t: TFunction, status: string): string {
    const s = String(status).toLowerCase();
    if (s === 'draft') return t('form.collectWorkspace.collectionBadgeDraft', 'Draft');
    if (s === 'active') return t('form.collectWorkspace.collectionBadgeActive', 'Active');
    if (s === 'closed') return t('form.collectWorkspace.collectionBadgeClosed', 'Closed');
    return status ? String(status).replace(/_/g, ' ') : '';
}

/** Classes badge statut — alignées sur home-pill-new / home-pill-ongoing */
function collectCollectionBadgeClassName(status: string): string {
    const s = String(status).toLowerCase();
    if (s === 'draft') return 'collect-badge draft';
    if (s === 'active') return 'collect-badge active';
    if (s === 'closed') return 'collect-badge closed';
    return 'collect-badge';
}

function iterGuideSubSections(g: any): any[] {
    if (g?.role_sections?.length) {
        const roles = [...g.role_sections].sort((a: any, b: any) => (a.order ?? 0) - (b.order ?? 0));
        const out: any[] = [];
        for (const rs of roles) {
            const label = String(rs.role_label ?? '').trim();
            const secs = [...(rs.sections || [])].sort((a: any, b: any) => (a.order ?? 0) - (b.order ?? 0));
            for (const sec of secs) {
                out.push({
                    ...sec,
                    name: label ? `${label} — ${sec.name ?? ''}`.trim() : sec.name,
                });
            }
        }
        return out;
    }
    return g?.sections ?? [];
}

function hasGuideContent(g: any) {
    const sections = iterGuideSubSections(g);
    if (!sections.length) return false;
    return sections.some((s: any) => (s.questions || []).some((q: any) => q.status === 'kept'));
}

function flattenKeptGuideQuestionTexts(g: any): string[] {
    const sections = iterGuideSubSections(g);
    if (!sections.length) return [];
    const out: string[] = [];
    for (const sec of sections) {
        const qs = [...(sec.questions || [])].sort((a: any, b: any) => (a.order ?? 0) - (b.order ?? 0));
        for (const q of qs) {
            if (q.status === 'removed' || q.status !== 'kept') continue;
            const text = String(q.text ?? '').trim();
            if (text) out.push(text);
        }
    }
    return out;
}

function collectTargetRolesAsInviteOptions(targetRoles: unknown): InviteRoleOption[] {
    return dedupeRoleOptionsPreservingLabels(Array.isArray(targetRoles) ? targetRoles : null);
}

const CollectDataWorkspaceView = () => {
    const { collectionId } = useParams<{ collectionId: string }>();
    const { t } = useTranslation();
    const auth = useAppSelector((s) => s.auth);
    const access_token = auth.access_token;
    const role = useAppSelector((s) => s.role.role);
    const navigate = useNavigate();
    const [tab, setTab] = useState<Tab>('collect');
    const [recentCollections, setRecentCollections] = useState<RecentCollectCollectionSummary[]>([]);
    const [recentLoading, setRecentLoading] = useState(false);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [infoBanner, setInfoBanner] = useState<string | null>(null);
    const [collection, setCollection] = useState<any>(null);
    const [guide, setGuide] = useState<any>(null);
    const [contributors, setContributors] = useState<any[]>([]);
    const [ownerMessages, setOwnerMessages] = useState<any[]>([]);
    const [ownerSetupPhase, setOwnerSetupPhase] = useState<OwnerSetupPhase | null>(null);
    const [setupInput, setSetupInput] = useState('');
    const [contextExcerpt, setContextExcerpt] = useState('');
    const [newContrib, setNewContrib] = useState({ name: '', email: '', role: '' });
    const [showAddRecipient, setShowAddRecipient] = useState(false);
    const [selectedContrib, setSelectedContrib] = useState<string | null>(null);
    const [thread, setThread] = useState<any[]>([]);
    const [generatingGuide, setGeneratingGuide] = useState(false);
    const [approvingGuide, setApprovingGuide] = useState(false);
    const [activeRoleKey, setActiveRoleKey] = useState<string | null>(null);
    const [streamBusy, setStreamBusy] = useState(false);
    const [assistantTyping, setAssistantTyping] = useState(false);
    const [voiceSessionActive, setVoiceSessionActive] = useState(false);
    const recipientsScrollRef = useRef<HTMLDivElement>(null);
    const addRecipientFormRef = useRef<HTMLDivElement>(null);
    const revealTimerRef = useRef<number | null>(null);
    const ownerSetupInFlightRef = useRef(false);

    const ownerMessageTimestamps = useMemo(
        () => ownerMessages.map((m) => m.created_at ?? m.createdAt),
        [ownerMessages],
    );

    const { threadScrollRef, scrollToBottom, scrollHeaderDisplay } = useInterviewThreadScroll({
        messageTimestamps: ownerMessageTimestamps,
        scrollDeps: [ownerMessages, assistantTyping, streamBusy, generatingGuide],
    });

    const [currentLanguage, setCurrentLanguage] = useState(() => {
        try {
            return localStorage.getItem('currentLanguage') || navigator.language;
        } catch {
            return 'en';
        }
    });
    const [chatTimeTick, setChatTimeTick] = useState(0);
    const [aiFeedbackById, setAiFeedbackById] = useState<Record<string, 'up' | 'down'>>({});
    const [editingMessageId, setEditingMessageId] = useState<string | null>(null);
    const [editDraft, setEditDraft] = useState('');
    const [ownerDeleteTargetId, setOwnerDeleteTargetId] = useState<string | null>(null);
    const [recentRenameRow, setRecentRenameRow] = useState<RecentCollectCollectionSummary | null>(null);
    const [recentDeleteRow, setRecentDeleteRow] = useState<RecentCollectCollectionSummary | null>(null);
    const [recentWithdrawRow, setRecentWithdrawRow] = useState<RecentCollectCollectionSummary | null>(null);
    const [recipientDeleteTarget, setRecipientDeleteTarget] = useState<{
        id: string;
        name?: string;
        email?: string;
    } | null>(null);
    const [recipientReplaceTarget, setRecipientReplaceTarget] = useState<{
        id: string;
        name?: string;
        email?: string;
    } | null>(null);
    const [contextFileBusy, setContextFileBusy] = useState(false);
    const [isAnalysisFileModalOpen, setIsAnalysisFileModalOpen] = useState(false);
    const [selectedMapIds, setSelectedMapIds] = useState<string[]>([]);
    const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
    const [expandedRoles, setExpandedRoles] = useState<Set<string>>(new Set());
    const inviteCloseRef = useRef<HTMLButtonElement>(null);

    const railNotificationMessages = useInterviewRailMessages();

    const clearRevealTimer = useCallback(() => {
        if (revealTimerRef.current != null) {
            window.clearInterval(revealTimerRef.current);
            revealTimerRef.current = null;
        }
    }, []);

    const scrollRecipientsPaneToEnd = useCallback(() => {
        requestAnimationFrame(() => {
            const el = recipientsScrollRef.current;
            if (!el) return;
            el.scrollTop = el.scrollHeight;
        });
    }, []);

    const openInviteRecipientModal = useCallback(() => {
        setError(null);
        const el = document.getElementById('inviteUser');
        if (el && (window as any).bootstrap?.Modal) {
            const Modal = (window as any).bootstrap.Modal;
            Modal.getOrCreateInstance(el).show();
        }
        scrollRecipientsPaneToEnd();
    }, [scrollRecipientsPaneToEnd]);

    const closeInviteRecipientModal = useCallback(() => {
        const el = document.getElementById('inviteUser');
        if (el && (window as any).bootstrap?.Modal) {
            (window as any).bootstrap.Modal.getInstance(el)?.hide();
        }
    }, []);

    useEffect(() => () => clearRevealTimer(), [clearRevealTimer]);

    const loadRecent = useCallback(async () => {
        if (!access_token) return;
        setRecentLoading(true);
        try {
            const r = await listRecentCollectCollections(access_token, 25);
            if (r.status === 200 && Array.isArray(r.body?.data?.collections)) {
                setRecentCollections(r.body.data.collections as RecentCollectCollectionSummary[]);
            }
        } finally {
            setRecentLoading(false);
        }
    }, [access_token]);

    const load = useCallback(async (opts?: { soft?: boolean; skipOwnerMessages?: boolean }): Promise<any | null> => {
        const soft = opts?.soft === true;
        const skipOwnerMessages = opts?.skipOwnerMessages === true;
        if (!collectionId || !access_token) return null;
        if (!soft) {
            setLoading(true);
            setError(null);
        }
        const res = await getCollectDataCollection(collectionId, access_token);
        if (res.status !== 200 || !res.body?.data) {
            if (!soft) setError(res.body?.description || 'Error');
            if (!soft) setLoading(false);
            return null;
        }
        const c = res.body.data.collection;
        setCollection(c);
        if (typeof c?.setup_phase === 'string' && c.setup_phase.trim()) {
            setOwnerSetupPhase(c.setup_phase.trim() as OwnerSetupPhase);
        }
        setGuide(res.body.data.guide);
        setContributors(res.body.data.contributors || []);
        if (c?.context_excerpt != null) setContextExcerpt(c.context_excerpt);
        if (c?.map_ids != null) setSelectedMapIds(c.map_ids);
        if (!skipOwnerMessages && !ownerSetupInFlightRef.current) {
            const om = await listOwnerSetupMessages(collectionId, access_token);
            if (om.status === 200 && om.body?.data?.messages) {
                const msgs = (om.body.data.messages as { content?: string }[]).filter(
                    (m) => String(m.content ?? '').trim().length > 0,
                );
                setOwnerMessages((prev) => {
                    if (msgs.length === 0 && prev.length > 0) return prev;
                    return msgs;
                });
            }
        }
        void loadRecent();
        if (!soft) setLoading(false);
        return c;
    }, [collectionId, access_token, loadRecent]);

    /** Passe la collecte en Active via POST /send. Retourne true si Active (200) ou déjà envoyée (409). */
    const sendCollectionForPublish = useCallback(async (): Promise<boolean> => {
            if (!collectionId || !access_token) return false;
            const res = await sendCollection(collectionId, access_token);
            if (res.status === 200 || res.status === 409) {
                await load({ soft: true, skipOwnerMessages: true });
                setError(null);
                return true;
            }
            setError(String(res.body?.description || t('words.error')));
            return false;
        },
        [collectionId, access_token, load, t],
    );

    const submitInviteContributors = useCallback(
        async (data: { emails: string[]; names: string[]; role: string }): Promise<InviteCustomSubmitResult> => {
            if (!collectionId || !access_token) {
                return { ok: false, message: t('words.error') };
            }
            const roleNorm = (data.role || '').trim();
            if (!roleNorm) {
                return { ok: false, message: t('navigation.selectRole') };
            }
            const roleKey = roleNorm.toLowerCase();
            const dupMsg = (emailDisplay: string) =>
                t('form.collectWorkspace.duplicateContributorRole', {
                    email: emailDisplay,
                    defaultValue: '{{email}} is already invited for this role.',
                });
            const seenKeys = new Set<string>();
            const inviteRows: { email: string; name: string }[] = [];
            for (const emailRaw of data.emails) {
                const email = emailRaw.trim();
                const em = email.toLowerCase();
                if (!em) continue;
                const k = `${em}|${roleKey}`;
                if (seenKeys.has(k)) {
                    return { ok: false, message: dupMsg(email) };
                }
                seenKeys.add(k);
                const already = contributors.some((c: { email?: string; role?: string }) => {
                    const ce = String(c.email ?? '').trim().toLowerCase();
                    const cr = String(c.role ?? '').trim().toLowerCase();
                    return ce === em && cr === roleKey;
                });
                if (already) {
                    return { ok: false, message: dupMsg(email) };
                }
                inviteRows.push({ email, name: email.split('@')[0] });
            }
            if (inviteRows.length === 0) {
                return { ok: false, message: t('navigation.enterAtLeastOneEmail') };
            }

            const results = await Promise.all(
                inviteRows.map(({ email, name }) =>
                    addContributor(collectionId, { name, email, role: roleNorm }, access_token),
                ),
            );

            let allSuccess = true;
            let lastMessage: string | undefined;
            results.forEach((res, i) => {
                if (collectContributorResponseFailed(res)) {
                    allSuccess = false;
                    const email = inviteRows[i].email;
                    const dup = responseHasDuplicateContributorRoleCode(res.body);
                    lastMessage = dup
                        ? dupMsg(email)
                        : res.body?.description || t('words.error');
                }
            });

            const refreshed = await load({ soft: true, skipOwnerMessages: true });
            if (allSuccess) {
                setError(null);
                if (
                    Boolean(refreshed?.guide_approved_at) &&
                    refreshed?.status === 'draft'
                ) {
                    const published = await sendCollectionForPublish();
                    if (published) {
                        setInfoBanner(
                            t(
                                'form.collectWorkspace.collectionActivatedSuccess',
                                'Collection is active. Invitation emails have been sent.',
                            ),
                        );
                        window.setTimeout(() => setInfoBanner(null), 8000);
                    }
                }
                return { ok: true };
            }
            return { ok: false, message: lastMessage || t('words.error') };
        },
        [access_token, collectionId, contributors, load, sendCollectionForPublish, t],
    );

    const updateLangPref = useCallback(
        async (lang: string) => {
            const language = lang === 'fr' ? 'french' : 'english';
            try {
                const updateData = {
                    fullname: auth.user.fullname,
                    phone_number: auth.user.phone_number,
                    lang: language,
                    updated_at: auth.user.created_at,
                };
                await FamlaServices('api_host', `/users/`, 'PUT', updateData, auth.access_token);
            } catch {
                /* ignore */
            }
        },
        [auth.user, auth.access_token],
    );

    const changeLanguage = useCallback(
        (language: string) => {
            const updateLanguage = language === 'fr-FR' || language === 'fr' ? 'fr' : 'en';
            i18n.changeLanguage(updateLanguage);
            void updateLangPref(updateLanguage);
            setCurrentLanguage(updateLanguage);
            try {
                localStorage.setItem('currentLanguage', updateLanguage);
            } catch {
                /* ignore */
            }
        },
        [updateLangPref],
    );

    useEffect(() => {
        changeLanguage(currentLanguage);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentLanguage]);

    useEffect(() => {
        if (!access_token) {
            navigate(Routes.AUTH_LOGIN);
            return;
        }
        load();
    }, [access_token, load, navigate]);

    useEffect(() => {
        if (tab !== 'collect' && tab !== 'monitor') return;
        const id = window.setInterval(() => setChatTimeTick((x) => x + 1), 6000);
        return () => clearInterval(id);
    }, [tab]);

    useEffect(() => {
        if (tab !== 'monitor' || !collectionId || !access_token) return;
        void load({ soft: true });
    }, [tab, collectionId, access_token, load]);

    useEffect(() => {
        if (editingMessageId && !ownerMessages.some((msg) => msg.id === editingMessageId)) {
            setEditingMessageId(null);
            setEditDraft('');
        }
    }, [ownerMessages, editingMessageId]);

    const runGenerateGuide = useCallback(
        async (stayOnCollect = false, collectionOverride?: any): Promise<boolean> => {
            if (!collectionId || !access_token) return false;
            const coll = collectionOverride ?? collection;
            if (!Boolean(coll?.ready_for_guide)) {
                setError(
                    t(
                        'form.collectWorkspace.setupNotReadyForScript',
                        'Finish setup in the Collect tab (objective, context or maps, and roles) before generating or approving the interview script.',
                    ),
                );
                return false;
            }
            setError(null);
            setGeneratingGuide(true);
            try {
                const res = await generateGuide(collectionId, access_token, contextExcerpt || undefined);
                if (res.status === 200 && res.body?.data?.guide) {
                    setGuide(res.body.data.guide);
                    if (!stayOnCollect) setTab('guide');
                    await load({ soft: true });
                    return true;
                }
                setError(
                    res.body?.description ||
                        t('form.collectWorkspace.guideGenerationFailed', 'Interview script generation failed'),
                );
                return false;
            } finally {
                setGeneratingGuide(false);
            }
        },
        [collectionId, access_token, collection, contextExcerpt, t, load],
    );

    const applyOwnerSetupTurnMeta = useCallback((data: OwnerSetupTurnResult) => {
        if (typeof data.setup_phase === 'string' && data.setup_phase.trim()) {
            setOwnerSetupPhase(data.setup_phase.trim() as OwnerSetupPhase);
        }
    }, []);

    const handleOwnerSetupTurnComplete = useCallback(
        async (data: OwnerSetupTurnResult) => {
            applyOwnerSetupTurnMeta(data);
            const freshCol = await load({ soft: true });
            if (data.ready_for_guide) {
                await runGenerateGuide(true, freshCol ?? undefined);
                await load({ soft: true });
            }
        },
        [applyOwnerSetupTurnMeta, load, runGenerateGuide],
    );

    const revealOwnerSetupMessages = useCallback(
        (messages: any[]): Promise<void> => {
            clearRevealTimer();
            return new Promise((resolve) => {
                let lastAi = -1;
                for (let i = messages.length - 1; i >= 0; i -= 1) {
                    if (!isOwnerSetupUserRole(String(messages[i]?.role ?? ''))) {
                        lastAi = i;
                        break;
                    }
                }
                if (lastAi < 0) {
                    setOwnerMessages(messages);
                    resolve();
                    return;
                }
                const fullText = String(messages[lastAi]?.content ?? '');
                if (fullText.length < 28) {
                    setOwnerMessages(messages);
                    resolve();
                    return;
                }
                const tail = messages[lastAi];
                const prefix = messages.slice(0, lastAi);
                setOwnerMessages([...prefix, { ...tail, content: '' }]);
                let pos = 0;
                const step = Math.max(2, Math.ceil(fullText.length / 26));
                revealTimerRef.current = window.setInterval(() => {
                    pos = Math.min(fullText.length, pos + step);
                    setOwnerMessages([...prefix, { ...tail, content: fullText.slice(0, pos) }]);
                    if (pos >= fullText.length) {
                        clearRevealTimer();
                        setOwnerMessages(messages);
                        resolve();
                    }
                }, 26);
            });
        },
        [clearRevealTimer],
    );

    const runOwnerSetupTurn = useCallback(
        async (body: { bootstrap?: boolean; content?: string }, restoreComposerOnError?: string) => {
            if (!collectionId || !access_token || ownerSetupInFlightRef.current) return false;
            ownerSetupInFlightRef.current = true;
            setStreamBusy(true);
            setAssistantTyping(true);
            setError(null);
            try {
                const res = await postOwnerSetupTurn(collectionId, access_token, body);
                if (res.status !== 200 || !res.body?.data) {
                    setError(res.body?.description || t('words.error'));
                    if (restoreComposerOnError != null) setSetupInput(restoreComposerOnError);
                    return false;
                }
                const data = res.body.data;
                setAssistantTyping(false);
                const msgs = Array.isArray(data.messages) ? data.messages : [];
                await revealOwnerSetupMessages(msgs);
                await handleOwnerSetupTurnComplete(data);
                return true;
            } catch {
                setError(t('form.collectWorkspace.connectionError', 'Connection error'));
                if (restoreComposerOnError != null) setSetupInput(restoreComposerOnError);
                return false;
            } finally {
                ownerSetupInFlightRef.current = false;
                setStreamBusy(false);
                setAssistantTyping(false);
            }
        },
        [access_token, collectionId, handleOwnerSetupTurnComplete, revealOwnerSetupMessages, t],
    );

    useEffect(() => {
        if (loading || !collectionId || !access_token) return;
        if (!collection || collection.status !== 'draft') return;
        if (ownerMessages.length > 0) return;
        if (ownerSetupInFlightRef.current || streamBusy) return;
        void runOwnerSetupTurn({ bootstrap: true });
    }, [loading, collectionId, access_token, collection?.status, ownerMessages.length, runOwnerSetupTurn, streamBusy]);

    const recipientsByRole = useMemo(() => {
        const m = new Map<string, any[]>();
        for (const c of contributors) {
            const r = (c.role && String(c.role).trim()) || 'Recipients';
            if (!m.has(r)) m.set(r, []);
            m.get(r)!.push(c);
        }
        return [...m.entries()].sort((a, b) => a[0].localeCompare(b[0]));
    }, [contributors]);

    const collectInviteAvailableRoles = useMemo(
        () => collectTargetRolesAsInviteOptions(collection?.target_roles),
        [collection?.target_roles],
    );

    useEffect(() => {
        setExpandedRoles(new Set());
    }, [collectionId]);

    useEffect(() => {
        if (!activeRoleKey && recipientsByRole.length) {
            setActiveRoleKey(recipientsByRole[0][0]);
        }
    }, [recipientsByRole, activeRoleKey]);

    const saveContext = async () => {
        if (!collectionId || !access_token) return;
        await patchCollectDataCollection(collectionId, { context_excerpt: contextExcerpt }, access_token);
        await load({ soft: true });
    };

    const sendOwnerSetupContent = async (messageText: string, restoreComposerOnError: string) => {
        if (!collectionId || !access_token || streamBusy || voiceSessionActive) return;
        const text = messageText.trim();
        if (!text) return;
        const optimisticId = `opt-${Date.now()}`;
        setOwnerMessages((prev) => [
            ...prev,
            { id: optimisticId, role: 'user', content: text, created_at: new Date().toISOString() },
        ]);
        const ok = await runOwnerSetupTurn({ content: text }, restoreComposerOnError);
        if (!ok) {
            setOwnerMessages((prev) => prev.filter((msg) => !String(msg?.id ?? '').startsWith('opt-')));
        }
    };

    const onCollectContextSkip = () => {
        void sendOwnerSetupContent(t('views.processInterview.skipContextAnswer'), setupInput);
    };

    const handleFileProcessing = async (
        file: File,
        options?: { sendChatMessage?: boolean },
    ): Promise<boolean> => {
        if (!collectionId || !access_token) return false;
        const sendChatMessage = options?.sendChatMessage !== false;
        setContextFileBusy(true);
        setError(null);
        try {
            const raw = (await extractDocumentText(file)).trim();
            if (!raw) {
                setError(t('form.collectWorkspace.contextFileEmpty'));
                return false;
            }
            const excerpt = raw.slice(0, 50000);
            await patchCollectDataCollection(
                collectionId,
                { context_excerpt: excerpt, context_skipped: false },
                access_token,
            );
            setContextExcerpt(excerpt);
            await load({ soft: true });
            if (sendChatMessage) {
                await sendOwnerSetupContent(
                    t('views.processInterview.contextFileAttachedNote', { fileName: file.name }),
                    setupInput,
                );
            }
            return true;
        } catch (err: unknown) {
            const msg = err instanceof Error ? err.message : '';
            if (msg === 'unsupported_type') {
                setError(t('form.collectWorkspace.contextFileUnsupported'));
            } else {
                setError(t('form.collectWorkspace.contextFileReadError'));
            }
            return false;
        } finally {
            setContextFileBusy(false);
        }
    };

    const handleAnalysisFileConfirm = async (options: { file?: File; files?: File[]; maps?: iMap[] }) => {
        const file = options.file ?? (options.files && options.files.length > 0 ? options.files[0] : undefined);
        const hasMaps = Boolean(options.maps && options.maps.length > 0);
        let fileAttached = false;
        let attachedFileName = '';

        if (file) {
            const ok = await handleFileProcessing(file, { sendChatMessage: false });
            if (ok) {
                fileAttached = true;
                attachedFileName = file.name;
            } else if (!hasMaps) {
                return;
            }
        }

        let mapNames = '';
        if (hasMaps && options.maps) {
            const mapIds = options.maps.map(m => m._id);
            setSelectedMapIds(mapIds);

            if (collectionId && access_token) {
                await patchCollectDataCollection(collectionId, { map_ids: mapIds, context_skipped: false }, access_token);
                await load({ soft: true });
                mapNames = options.maps.map(m => m.name).join(', ');
            }
        }

        const chatParts: string[] = [];
        if (fileAttached) {
            chatParts.push(
                t('views.processInterview.contextFileAttachedNote', { fileName: attachedFileName }),
            );
        }
        if (mapNames) {
            chatParts.push(
                t('views.processInterview.contextMapsAttachedNote', { mapNames }),
            );
        }
        if (chatParts.length > 0) {
            await sendOwnerSetupContent(chatParts.join('\n'), setupInput);
        }
    };

    const reloadGuide = async () => {
        if (!collectionId || !access_token) return;
        const g = await getGuide(collectionId, access_token);
        if (g.status === 200) setGuide(g.body.data);
    };

    const onApproveGuide = async () => {
        if (!collectionId || !access_token || approvingGuide) return;
        if (!Boolean(collection?.ready_for_guide)) {
            setError(
                t(
                    'form.collectWorkspace.setupNotReadyForScript',
                    'Finish setup in the Collect tab (objective, context or maps, and roles) before generating or approving the interview script.',
                ),
            );
            return;
        }
        setApprovingGuide(true);
        setError(null);
        try {
            const res = await approveGuide(collectionId, access_token);
            if (res.status !== 200) {
                setError(res.body?.description || t('words.error'));
                return;
            }
            const hadRecipients = contributors.length > 0;
            const ok = await sendCollectionForPublish();
            if (ok) {
                await load({ soft: true, skipOwnerMessages: true });
                setTab('collect');
                setInfoBanner(
                    t(
                        hadRecipients
                            ? 'form.collectWorkspace.approveAndPublishSuccess'
                            : 'form.collectWorkspace.approveAndPublishLinkSuccess',
                        hadRecipients
                            ? 'Script approved. The collection is active and invitations have been sent.'
                            : 'Script approved. The collection is active — share the responder link with respondents.',
                    ),
                );
            } else {
                await load({ soft: true, skipOwnerMessages: true });
                setInfoBanner(
                    t(
                        'form.collectWorkspace.approveActivationFailed',
                        'The collection could not be activated. Please try again.',
                    ),
                );
            }
            window.setTimeout(() => setInfoBanner(null), 8000);
        } finally {
            setApprovingGuide(false);
        }
    };

    const onAddContributor = async () => {
        if (!collectionId || !access_token || !newContrib.name || !newContrib.email) return;
        const res = await addContributor(collectionId, newContrib, access_token);
        if (!collectContributorResponseFailed(res) && res.body?.data) {
            const added = res.body.data;
            if (Array.isArray(added)) {
                setContributors(added);
            } else {
                setContributors((prev) => [...prev, added]);
            }
            setNewContrib({ name: '', email: '', role: '' });
            setShowAddRecipient(false);
            setError(null);
        } else {
            const dup = responseHasDuplicateContributorRoleCode(res.body);
            setError(
                dup
                    ? t('form.collectWorkspace.duplicateContributorRole', {
                          email: newContrib.email.trim(),
                          defaultValue: '{{email}} is already invited for this role.',
                      })
                    : res.body?.description || t('words.error'),
            );
        }
    };

    const goToRecentCollection = useCallback(
        (row: RecentCollectCollectionSummary) => {
            if (row.id === collectionId) return;
            const inviteTok = row.contributor_invitation_token?.trim();
            if (row.opened_as_contributor && inviteTok) {
                navigate(`/r/${inviteTok}`);
                return;
            }
            navigate(`/process/data-collect/${row.id}`);
        },
        [collectionId, navigate],
    );

    const handleRecentRename = useCallback(
        (row: RecentCollectCollectionSummary) => {
            if (!access_token || !canManageDataCollectionSummary(row)) return;
            setRecentRenameRow(row);
        },
        [access_token],
    );

    const executeRecentRename = useCallback(
        async (trimmed: string): Promise<boolean> => {
            if (!access_token || !recentRenameRow) return false;
            const res = await patchCollectDataCollection(recentRenameRow.id, { name: trimmed }, access_token);
            if (res.status !== 200) {
                setError(res.body?.description || t('words.error'));
                return false;
            }
            setError(null);
            await loadRecent();
            if (recentRenameRow.id === collectionId) await load({ soft: true });
            return true;
        },
        [access_token, collectionId, load, loadRecent, recentRenameRow, t],
    );

    const executeRecentDelete = useCallback(async (): Promise<boolean> => {
        if (!access_token || !recentDeleteRow || !canManageDataCollectionSummary(recentDeleteRow)) return false;
        setError(null);
        const res = await deleteCollectDataCollection(recentDeleteRow.id, access_token);
        if (res.status !== 200) {
            setError(res.body?.description || t('words.error'));
            return false;
        }
        const deletedId = recentDeleteRow.id;
        await loadRecent();
        if (deletedId === collectionId) {
            navigate(Routes.DATA_COLLECT);
        }
        return true;
    }, [access_token, collectionId, loadRecent, navigate, recentDeleteRow, t]);

    const executeRecentWithdraw = useCallback(async (): Promise<boolean> => {
        if (!access_token || !recentWithdrawRow || !recentWithdrawRow.opened_as_contributor) return false;
        if (canManageDataCollectionSummary(recentWithdrawRow)) return false;
        setError(null);
        const { ok } = await withdrawRecentCollectContribution(recentWithdrawRow, access_token);
        if (!ok) {
            setError(t('words.error'));
            return false;
        }
        await loadRecent();
        return true;
    }, [access_token, loadRecent, recentWithdrawRow, t]);

    const executeDeleteRecipient = useCallback(async (): Promise<boolean> => {
        if (!collectionId || !access_token || !recipientDeleteTarget) return false;
        const res = await deleteCollectContributor(collectionId, recipientDeleteTarget.id, access_token);
        if (res.status === 200 && Array.isArray(res.body?.data)) {
            setContributors(res.body.data);
            if (selectedContrib === recipientDeleteTarget.id) {
                setSelectedContrib('');
                setThread([]);
            }
            setError(null);
            return true;
        }
        setError(res.body?.description || t('words.error'));
        return false;
    }, [collectionId, access_token, recipientDeleteTarget, selectedContrib, t]);

    const executeReplaceRecipient = useCallback(async (): Promise<boolean> => {
        if (!collectionId || !access_token || !recipientReplaceTarget) return false;
        const res = await deleteCollectContributor(collectionId, recipientReplaceTarget.id, access_token);
        if (res.status === 200 && Array.isArray(res.body?.data)) {
            setContributors(res.body.data);
            if (selectedContrib === recipientReplaceTarget.id) {
                setSelectedContrib('');
                setThread([]);
            }
            setError(null);
            openInviteRecipientModal();
            return true;
        }
        setError(res.body?.description || t('words.error'));
        return false;
    }, [collectionId, access_token, openInviteRecipientModal, recipientReplaceTarget, selectedContrib, t]);

    const handleRemindRecipient = useCallback(
        async (c: { id: string }) => {
            if (!collectionId || !access_token) return;
            setInfoBanner(null);
            const res = await remindCollectContributor(collectionId, c.id, access_token);
            if (res.status === 200) {
                setInfoBanner(t('form.collectWorkspace.recipientReminderSent', 'Reminder email sent.'));
                window.setTimeout(() => setInfoBanner(null), 5000);
                await load({ soft: true });
            } else {
                setError(res.body?.description || t('words.error'));
            }
        },
        [collectionId, access_token, load, t],
    );

    const openDeleteRecipient = useCallback(
        (c: { id: string; name?: string; email?: string }) => {
            if (!collectionId || !access_token) return;
            setRecipientDeleteTarget(c);
        },
        [collectionId, access_token],
    );

    const openReplaceRecipient = useCallback(
        (c: { id: string; name?: string; email?: string }) => {
            if (!collectionId || !access_token) return;
            setRecipientReplaceTarget(c);
        },
        [collectionId, access_token],
    );

    const openThread = async (cid: string) => {
        if (!collectionId || !access_token) return;
        setSelectedContrib(cid);
        const res = await getContributorThreadForOwner(collectionId, cid, access_token);
        if (res.status === 200 && res.body?.data?.messages) {
            setThread(res.body.data.messages);
            void load({ soft: true });
        } else setThread([]);
    };

    const chatReferenceNow = useMemo(
        () => new Date(),
        [chatTimeTick, ownerMessages, assistantTyping, streamBusy, thread],
    );

    const ownerDisplayName = useMemo(() => {
        const n = auth.user?.fullname?.trim();
        if (n) return n;
        const e = auth.user?.email?.trim();
        if (e) return e.split('@')[0] || e;
        return t('words.me');
    }, [auth.user?.fullname, auth.user?.email, t]);

    const isOwnerUser = (role: string) => role === 'user' || role === 'owner';

    const monitorGuideIntents = useMemo(() => flattenKeptGuideQuestionTexts(guide), [guide]);
    const monitorAssistantMessages = useMemo(
        () => thread.filter((m) => !isOwnerUser(String(m?.role ?? ''))),
        [thread],
    );

    const lastOwnerMessage = useMemo(() => {
        for (let i = ownerMessages.length - 1; i >= 0; i -= 1) {
            const m = ownerMessages[i];
            if (isOwnerUser(String(m?.role ?? ''))) return m;
        }
        return null;
    }, [ownerMessages]);

    const pendingOwnerVoiceQuestion = useMemo(() => {
        for (let i = ownerMessages.length - 1; i >= 0; i -= 1) {
            const m = ownerMessages[i];
            if (!isOwnerUser(String(m?.role ?? ''))) {
                return String(m?.content ?? '').trim();
            }
        }
        return '';
    }, [ownerMessages]);

    /** Show typing while waiting; hide on bootstrap only if assistant reply already visible (load race). */
    const showOwnerTypingBubble = useMemo(() => {
        if (!streamBusy || !assistantTyping) return false;
        const lastMsg = ownerMessages[ownerMessages.length - 1];
        if (lastMsg && isOwnerSetupUserRole(String(lastMsg?.role ?? ''))) {
            return true;
        }
        const hasAssistantReply = ownerMessages.some(
            (m) => !isOwnerSetupUserRole(String(m?.role ?? '')) && String(m?.content ?? '').trim().length > 0,
        );
        return !hasAssistantReply;
    }, [streamBusy, assistantTyping, ownerMessages]);

    const beginEditMessage = useCallback((m: { id: string; content?: string }) => {
        setEditingMessageId(m.id);
        setEditDraft(m.content || '');
    }, []);

    const cancelEditMessage = useCallback(() => {
        setEditingMessageId(null);
        setEditDraft('');
    }, []);

    const saveEditMessage = useCallback(async () => {
        if (!editingMessageId || !collectionId || !access_token) return;
        const text = editDraft.trim();
        if (!text) return;
        if (String(editingMessageId).startsWith('opt-')) {
            setOwnerMessages((prev) => prev.map((m) => (m.id === editingMessageId ? { ...m, content: text } : m)));
            cancelEditMessage();
            return;
        }
        setError(null);
        const res = await patchOwnerSetupMessage(collectionId, editingMessageId, text, access_token);
        if (res.status === 200 && res.body?.data?.messages) {
            setOwnerMessages(res.body.data.messages);
            cancelEditMessage();
            await load({ soft: true });
        } else {
            setError(res.body?.description || 'Update failed');
        }
    }, [editingMessageId, collectionId, access_token, editDraft, cancelEditMessage, load]);

    const deleteOwnerSetupFromMessage = useCallback(
        async (messageId: string): Promise<boolean> => {
            if (!collectionId || !access_token) return false;
            if (String(messageId).startsWith('opt-')) {
                setOwnerMessages((prev) => prev.filter((m) => m.id !== messageId));
                cancelEditMessage();
                return true;
            }
            setError(null);
            const res = await deleteOwnerSetupMessage(collectionId, messageId, access_token);
            if (res.status === 200 && res.body?.data?.messages) {
                setOwnerMessages(res.body.data.messages);
                cancelEditMessage();
                await load({ soft: true });
                return true;
            }
            setError(res.body?.description || t('form.collectWorkspace.ownerSetupDeleteFailed'));
            return false;
        },
        [collectionId, access_token, t, cancelEditMessage, load],
    );

    const renderAiHoverActions = (messageKey: string, show: boolean) => {
        if (!show) return null;
        const thumb = aiFeedbackById[messageKey];
        return (
            <div className="ai-actions">
                <button
                    type="button"
                    className={`msg-btn ${thumb === 'up' ? 'liked' : ''}`}
                    title={t('form.collectWorkspace.likeTitle')}
                    onClick={() =>
                        setAiFeedbackById((prev) => {
                            const next = { ...prev };
                            if (next[messageKey] === 'up') delete next[messageKey];
                            else next[messageKey] = 'up';
                            return next;
                        })
                    }
                >
                    <svg viewBox="0 0 16 16" fill="none" aria-hidden>
                        <path
                            d="M5 7V13H3C2.4 13 2 12.6 2 12V8C2 7.4 2.4 7 3 7H5ZM5 7L7 2C7.8 2 9 2.4 9 4V6H12.5C13.1 6 13.5 6.5 13.4 7.1L12.4 12.1C12.3 12.6 11.9 13 11.4 13H5V7Z"
                            stroke="currentColor"
                            strokeWidth="1.2"
                            strokeLinejoin="round"
                        />
                    </svg>
                </button>
                <button
                    type="button"
                    className={`msg-btn danger ${thumb === 'down' ? 'selected' : ''}`}
                    title={t('form.collectWorkspace.dislikeTitle')}
                    onClick={() =>
                        setAiFeedbackById((prev) => {
                            const next = { ...prev };
                            if (next[messageKey] === 'down') delete next[messageKey];
                            else next[messageKey] = 'down';
                            return next;
                        })
                    }
                >
                    <svg viewBox="0 0 16 16" fill="none" aria-hidden>
                        <path
                            d="M11 9V3H13C13.6 3 14 3.4 14 4V8C14 8.6 13.6 9 13 9H11ZM11 9L9 14C8.2 14 7 13.6 7 12V10H3.5C2.9 10 2.5 9.5 2.6 8.9L3.6 3.9C3.7 3.4 4.1 3 4.6 3H11V9Z"
                            stroke="currentColor"
                            strokeWidth="1.2"
                            strokeLinejoin="round"
                        />
                    </svg>
                </button>
                <button type="button" disabled className="msg-btn" title={t('form.collectWorkspace.listenTitle')} onClick={() => {}}>
                    <svg viewBox="0 0 16 16" fill="none" aria-hidden="true">
                        <rect x="5" y="1" width="6" height="9" rx="3" stroke="currentColor" strokeWidth="1.2" />
                        <path d="M3 8C3 10.8 5.2 13 8 13C10.8 13 13 10.8 13 8" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" />
                        <path d="M8 13V15" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" />
                    </svg>
                </button>
            </div>
        );
    };

    /** Prévisualisation locale : navigation dans le même onglet (évite session Redux « vide » dans un tab `window.open`). */
    const openViewAsRecipient = useCallback(() => {
        setError(null);
        if (!collectionId) return;
        navigate(`/process/data-collect/${collectionId}/recipient-preview`);
    }, [collectionId, navigate]);

    /** True once the owner has sent at least one setup message (objective often lives in chat before `objective_text` is persisted). */
    const collectSetupHasUserTurn = useMemo(
        () =>
            ownerMessages.some(
                (m) => isOwnerUser(m.role) && String(m.content ?? '').trim().length > 0,
            ),
        [ownerMessages],
    );

    
    const ownerExplicitContextSkip = useMemo(() => {
        const normalizedUserTurns = ownerMessages
            .filter((m) => isOwnerUser(String(m?.role ?? '')))
            .map((m) => String(m?.content ?? '').trim().toLowerCase())
            .filter(Boolean);
        if (normalizedUserTurns.length === 0) return false;

        const skipAnswer = String(
            t('views.processInterview.skipContextAnswer', { defaultValue: 'No additional context for now.' }) ?? '',
        )
            .trim()
            .toLowerCase();
        const explicitSkipMarkers = [
            skipAnswer,
            'no additional context',
            'skip context',
            'no context for now',
            'not now',
            'pas de contexte',
            'je passe',
            'pas pour le moment',
            'je n ai pas de contexte',
        ].filter(Boolean);

        return normalizedUserTurns.some((turn) => explicitSkipMarkers.some((marker) => turn.includes(marker)));
    }, [ownerMessages, t]);

    const ownerAttachedContextFile = useMemo(
        () =>
            ownerMessages.some(
                (m) =>
                    isOwnerSetupUserRole(String(m?.role ?? '')) &&
                    ownerMessageIndicatesContextFile(String(m?.content ?? ''), t),
            ),
        [ownerMessages, t],
    );

    const contextChecklistDetailDone = useMemo(
        () =>
            formatContextChecklistDetailDone(
                collection?.map_ids?.length || 0,
                ownerAttachedContextFile,
                t,
            ),
        [collection?.map_ids, ownerAttachedContextFile, t],
    );

    
    const recentCollectionsVisible = useMemo(() => {
        const rows = Array.isArray(recentCollections) ? [...recentCollections] : [];
        if (!collectionId || !collection) return rows;
        const currentRow: RecentCollectCollectionSummary = {
            id: collectionId,
            name: String(collection?.name ?? '').trim() || t('form.collectWorkspace.collectionNameFallback', { defaultValue: 'Untitled collection' }),
            status: String(collection?.status ?? 'draft'),
            created_at: collection?.created_at ?? undefined,
            updated_at: collection?.updated_at ?? collection?.created_at ?? new Date().toISOString(),
        };
        const idx = rows.findIndex((r) => r.id === collectionId);
        if (idx >= 0) rows[idx] = { ...rows[idx], ...currentRow };
        else rows.unshift(currentRow);
        rows.sort((a, b) => {
            const ta = new Date((a.updated_at ?? a.created_at ?? '') as string).getTime() || 0;
            const tb = new Date((b.updated_at ?? b.created_at ?? '') as string).getTime() || 0;
            return tb - ta;
        });
        return rows;
    }, [recentCollections, collectionId, collection, t]);

    const objectiveDone =
        Boolean(collection?.objective_satisfied) ||
        (Boolean(collection?.owner_setup_what_clear) && Boolean(collection?.owner_setup_why_clear)) ||
        countOwnerSetupUserTurns(ownerMessages, t) >= 2;
    const contextSatisfied =
        Boolean(collection?.context_satisfied) ||
        Boolean(collection?.context_skipped) ||
        ownerExplicitContextSkip;
    /** Contexte : jamais « fait » tant que l’objectif (what+why) n’est pas validé */
    const contextDone = objectiveDone && contextSatisfied;

    /** Phase API (setup_phase) ; fallback minimal si absent */
    const effectiveSetupPhase = useMemo((): OwnerSetupPhase | null => {
        const clampObjectivePhase = (): OwnerSetupPhase => {
            if (!collection?.owner_setup_what_clear && countOwnerSetupUserTurns(ownerMessages, t) < 1) {
                return 'objective_what';
            }
            return 'objective_why';
        };

        let phase: OwnerSetupPhase | null = null;
        if (ownerSetupPhase) phase = ownerSetupPhase;
        else if (typeof collection?.setup_phase === 'string' && collection.setup_phase.trim()) {
            phase = collection.setup_phase.trim() as OwnerSetupPhase;
        }

        if (!collection || collection.status !== 'draft') return 'complete';
        if (!objectiveDone) {
            if (
                phase === 'awaiting_context'
                || phase === 'roles'
                || phase === 'invites'
                || phase === 'complete'
            ) {
                return clampObjectivePhase();
            }
            if (phase) return phase;
            return clampObjectivePhase();
        }
        if (!contextDone) return 'awaiting_context';
        if (phase && phase !== 'awaiting_context' && phase !== 'objective_what' && phase !== 'objective_why') {
            return phase;
        }
        if (!collection.roles_confirmed) return 'roles';
        return collection.ready_for_guide ? 'complete' : 'roles';
    }, [ownerSetupPhase, collection, objectiveDone, contextDone, ownerMessages, t]);
    const guideReady = hasGuideContent(guide);
    const guideApproved = Boolean(collection?.guide_approved_at);
    const readyForGuide = Boolean(collection?.ready_for_guide);
    const hasRecipients = contributors.length > 0;
    const interviewStats = useMemo(() => ({
        total: contributors.length,
        completed: contributors.filter(
            (c: { status?: string }) => String(c.status).toLowerCase() === 'completed',
        ).length,
    }), [contributors]);

    const checklistSteps = [
        {
            key: 'obj',
            label: t('form.collectWorkspace.checklist.obj.label'),
            detail: objectiveDone
                ? t('form.collectWorkspace.checklist.obj.detailDone')
                : t('form.collectWorkspace.checklist.obj.detailPending'),
            done: objectiveDone,
            active: !objectiveDone,
        },
        {
            key: 'ctx',
            label: t('form.collectWorkspace.checklist.ctx.label'),
            detail: !objectiveDone
                ? t('form.collectWorkspace.checklist.ctx.detailPending')
                : ownerExplicitContextSkip || collection?.context_skipped
                ? t('form.collectWorkspace.checklist.ctx.detailSkipped')
                : contextDone
                    ? contextChecklistDetailDone
                    : t('form.collectWorkspace.checklist.ctx.detailPending'),
            done: contextDone,
            active: objectiveDone && !contextDone,
        },
        {
            key: 'guide',
            label: t('form.collectWorkspace.checklist.guide.label'),
            detail: generatingGuide
                ? t('form.collectWorkspace.checklist.guide.detailGenerating')
                : guideApproved
                    ? t('form.collectWorkspace.checklist.guide.detailApproved')
                    : guideReady && !readyForGuide
                        ? t(
                              'form.collectWorkspace.checklist.guide.detailSetupBlocked',
                              'Finish Collect setup before approving the script.',
                          )
                    : guideReady
                        ? t('form.collectWorkspace.checklist.guide.detailReady')
                        : t('form.collectWorkspace.checklist.guide.detailPending'),
            done: guideApproved,
            active:
                objectiveDone
                && contextDone
                && readyForGuide
                && !guideApproved,
        },
        {
            key: 'rec',
            label: t('form.collectWorkspace.checklist.rec.label'),
            detail: hasRecipients
                ? t('form.collectWorkspace.checklist.rec.detailDone', { count: contributors.length })
                : t('form.collectWorkspace.checklist.rec.detailPending'),
            done: hasRecipients,
            active: guideApproved && !hasRecipients,
        },
        {
            key: 'interviews',
            label: t('form.collectWorkspace.checklist.interviews.label'),
            detail: t('form.collectWorkspace.checklist.interviews.detailProgress', {
                completed: interviewStats.completed,
                total: interviewStats.total,
            }),
            done: interviewStats.total > 0 && interviewStats.completed === interviewStats.total,
            active:
                guideApproved
                && interviewStats.total > 0
                && interviewStats.completed < interviewStats.total,
        },
    ];

    const showCollectContextCta =
        objectiveDone &&
        effectiveSetupPhase === 'awaiting_context' &&
        !contextDone &&
        collection?.status === 'draft' &&
        !streamBusy &&
        !generatingGuide &&
        collectSetupHasUserTurn;

    const showGuideReadyCta =
        tab === 'collect' &&
        guideReady &&
        !guideApproved &&
        !streamBusy &&
        !generatingGuide &&
        !approvingGuide;

    const showApproveRetryCta =
        tab === 'collect' &&
        guideApproved &&
        collection?.status === 'draft' &&
        !streamBusy &&
        !generatingGuide &&
        !approvingGuide;

    /** Tant qu’un bandeau d’action setup est affiché, on ne peut pas contourner par la saisie ou la voix. */
    const setupCtaBlocksOwnerInput =
        showCollectContextCta || showGuideReadyCta || showApproveRetryCta;

    const setupCtaInputLockedHint = useMemo(() => {
        if (showCollectContextCta) {
            return t('form.collectWorkspace.contextChoiceInputLockedHint');
        }
        if (showGuideReadyCta) {
            return t('form.collectWorkspace.guideCtaInputLockedHint');
        }
        if (showApproveRetryCta) {
            return t('form.collectWorkspace.approveRetryInputLockedHint');
        }
        return undefined;
    }, [showCollectContextCta, showGuideReadyCta, showApproveRetryCta, t]);

    const postSetup = async () => {
        if (voiceSessionActive || setupCtaBlocksOwnerInput) return;
        if (!setupInput.trim()) return;
        const draft = setupInput.trim();
        setSetupInput('');
        await sendOwnerSetupContent(draft, draft);
    };

    const useFullLaunchChecklist = collection?.status === 'draft' || collection?.status === 'active';
    const launchChecklistProgressPct =
        checklistSteps.length > 0
            ? Math.round((checklistSteps.filter((s) => s.done).length / checklistSteps.length) * 100)
            : 0;

    const handleChecklistClick = useCallback(
        (key: string) => {
            if (
                key === 'guide'
                && (readyForGuide || guideReady || generatingGuide || guideApproved)
            ) {
                setTab('guide');
                void reloadGuide();
                return;
            }
            if (key === 'obj' || key === 'ctx') {
                setTab('collect');
                return;
            }
            if (key === 'rec') {
                setTab('collect');
                openInviteRecipientModal();
                return;
            }
        },
        [
            generatingGuide,
            guideApproved,
            guideReady,
            hasRecipients,
            readyForGuide,
            reloadGuide,
            openInviteRecipientModal,
        ],
    );

    const guideActionToolbar = (extraClass = '') => (
        <div className={`collect-guide-toolbar-row ${extraClass}`.trim()}>
            {collection?.status === 'draft' && hasGuideContent(guide) ? (
                <>
                    <button
                        type="button"
                        className="action-btn"
                        disabled={generatingGuide || streamBusy || !readyForGuide || guideApproved}
                        title={
                            !readyForGuide
                                ? t(
                                      'form.collectWorkspace.setupNotReadyShort',
                                      'Complete setup in Collect first.',
                                  )
                                : undefined
                        }
                        onClick={() => void runGenerateGuide(false)}
                        aria-busy={generatingGuide}
                    >
                        {generatingGuide
                            ? t('form.collectWorkspace.generatingGuide')
                            : t('form.collectWorkspace.regenerateGuide')}
                    </button>
                    {!guideApproved ? (
                        <button
                            type="button"
                            className="action-btn primary"
                            disabled={generatingGuide || streamBusy || !readyForGuide || approvingGuide}
                            title={
                                !readyForGuide
                                    ? t(
                                          'form.collectWorkspace.setupNotReadyShort',
                                          'Complete setup in Collect first.',
                                      )
                                    : undefined
                            }
                            onClick={() => void onApproveGuide()}
                            aria-busy={approvingGuide}
                        >
                            {approvingGuide
                                ? t('form.collectWorkspace.approvingGuide', 'Approving…')
                                : t('form.collectWorkspace.approveGuide')}
                        </button>
                    ) : collection?.status === 'draft' ? (
                        <button
                            type="button"
                            className="action-btn primary"
                            disabled={generatingGuide || streamBusy || approvingGuide}
                            onClick={() => void onApproveGuide()}
                            aria-busy={approvingGuide}
                        >
                            {approvingGuide
                                ? t('form.collectWorkspace.approvingGuide', 'Approving…')
                                : t('form.collectWorkspace.approveGuide')}
                        </button>
                    ) : null}
                </>
            ) : null}
            <button type="button" className="action-btn" onClick={openViewAsRecipient}>
                {t('form.collectWorkspace.viewAsRecipient')}
            </button>
        </div>
    );

    const guideAiAdaptBannerNote = () => (
        <div className="collect-guide-owner-banner" role="note">
            {t(
                'form.collectWorkspace.guideAiAdaptBanner',
                '{{appName}} will adapt how each question is worded based on role and prior answers — this script lists intents, not fixed recipient wording.',
            )}
        </div>
    );

    if (!collectionId) return null;

    return (
        <AppLayout>
            <>
                <div className="pc-wrap collect-workspace-root">
                    {infoBanner && (
                        <div className="collect-alert collect-alert--info" style={{ margin: '0 16px 8px', flexShrink: 0 }} role="status">
                            {infoBanner}
                        </div>
                    )}
                    {error && (
                        <div className="collect-alert danger" style={{ margin: '0 16px 8px', flexShrink: 0 }} role="alert">
                            <span className="collect-alert__msg">{error}</span>
                        </div>
                    )}
                    <div className="interview-shell">
                        <div className="shell">

                            {/* ── LEFT PANEL ── */}
                            <div className="left-panel">
                                <div className="lp-header">
                                    <div className="back-btn" onClick={() => navigate(Routes.HOMEVIEW)}>
                                        <svg style={{ width: 12, height: 12 }} viewBox="0 0 16 16" fill="none">
                                            <path d="M10 3L5 8L10 13" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
                                        </svg>
                                        {t('words.home')}
                                    </div>
                                </div>
                                <div className="lp-section-scrollable" ref={recipientsScrollRef}>
                                    <div className="lp-section">
                                        <div className="lp-label">{t('form.collectWorkspace.recipientsTitle', 'Recipients')}</div>
                                        {!loading && recipientsByRole.length === 0 && (
                                            <p className="collect-muted" style={{ fontSize: '0.78rem' }}>
                                                {t('form.collectWorkspace.noRecipients', 'No recipients added yet')}
                                            </p>
                                        )}
                                        {recipientsByRole.map(([roleKey, people]) => {
                                            const isCollapsed = !expandedRoles.has(roleKey);
                                            return (
                                                <div className="role-block" key={roleKey}>
                                                    <div
                                                        className={`role-header ${!isCollapsed ? 'active' : ''}`}
                                                        role="button"
                                                        tabIndex={0}
                                                        onClick={() => {
                                                            setExpandedRoles(prev => {
                                                                const next = new Set(prev);
                                                                if (next.has(roleKey)) next.delete(roleKey);
                                                                else next.add(roleKey);
                                                                return next;
                                                            });
                                                        }}
                                                    >
                                                        <div className="role-dot" />
                                                        <span className="role-name">{roleKey}</span>
                                                        <div className="role-header-end">
                                                            <span className={`role-arrow ${!isCollapsed ? 'open' : ''}`} aria-hidden>
                                                                <svg width="10" height="6" viewBox="0 0 10 6" fill="none"><path d="M1 1L5 5L9 1" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" /></svg>
                                                            </span>
                                                            <span className="role-count">{people.length}</span>
                                                        </div>
                                                    </div>
                                                    {!isCollapsed && (
                                                        <div className="role-members" style={{ borderLeft: '1px solid rgba(255,255,255,0.05)', marginLeft: 6, paddingLeft: 8 }}>
                                                            {people.map((c) => {
                                                                const active = selectedContrib === c.id;
                                                                return (
                                                                    <div
                                                                        key={c.id}
                                                                        className={`role-person role-person--clickable collect-ws-recipient-row ${active ? 'active' : ''}`}
                                                                        tabIndex={0}
                                                                        onClick={() => {
                                                                            setTab('monitor');
                                                                            void openThread(c.id);
                                                                        }}
                                                                        onKeyDown={(e) => {
                                                                            if (e.key === 'Enter' || e.key === ' ') {
                                                                                e.preventDefault();
                                                                                setTab('monitor');
                                                                                void openThread(c.id);
                                                                            }
                                                                        }}
                                                                    >
                                                                        <div className="role-person-left">
                                                                            <div className="mbadge mb-guest">
                                                                                {initialsFromName(contributorDisplayName(c))}
                                                                            </div>
                                                                            <div className="role-person-info">
                                                                                <span className="role-person-name">{contributorDisplayName(c)}</span>
                                                                                <div className="collect-ws-recipient-meta">
                                                                                    <span className={`status-dot ${c.status === 'completed' ? 'done' : ''}`} />
                                                                                    <span className="collect-ws-recipient-status-text">
                                                                                        {c.status === 'completed'
                                                                                            ? t('words.completed', 'Completed')
                                                                                            : t('words.pending', 'Pending')}
                                                                                    </span>
                                                                                </div>
                                                                            </div>
                                                                        </div>
                                                                        <div
                                                                            className="collect-ws-recipient-menu"
                                                                            onClick={(e) => e.stopPropagation()}
                                                                            onKeyDown={(e) => e.stopPropagation()}
                                                                        >
                                                                            <div className="dropdown">
                                                                                <button
                                                                                    type="button"
                                                                                    className="btn btn-link text-secondary text-decoration-none p-1 collect-ws-recipient-menu__toggle"
                                                                                    data-bs-toggle="dropdown"
                                                                                    aria-expanded="false"
                                                                                    aria-label={t('form.collectWorkspace.recipientRowMenu', 'Recipient actions')}
                                                                                    onClick={(e) => e.stopPropagation()}
                                                                                >
                                                                                    <i className="fa fa-ellipsis-vertical small" aria-hidden />
                                                                                </button>
                                                                                <ul className="dropdown-menu dropdown-menu-dark dropdown-menu-end small border border-secondary shadow-sm">
                                                                                    <li>
                                                                                        <button
                                                                                            type="button"
                                                                                            className="dropdown-item"
                                                                                            onClick={() => void handleRemindRecipient(c)}
                                                                                        >
                                                                                            {t('navigation.sendReminder', 'Send reminder')}
                                                                                        </button>
                                                                                    </li>
                                                                                    <li>
                                                                                        <button
                                                                                            type="button"
                                                                                            className="dropdown-item"
                                                                                            onClick={() => openReplaceRecipient(c)}
                                                                                        >
                                                                                            {t('words.replace', 'Replace')}
                                                                                        </button>
                                                                                    </li>
                                                                                    <li>
                                                                                        <button
                                                                                            type="button"
                                                                                            className="dropdown-item text-danger"
                                                                                            onClick={() => openDeleteRecipient(c)}
                                                                                        >
                                                                                            {t('words.delete', 'Delete')}
                                                                                        </button>
                                                                                    </li>
                                                                                </ul>
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                );
                                                            })}
                                                        </div>
                                                    )}
                                                </div>
                                            );
                                        })}
                                        {guideApproved ? (
                                        <div
                                            className="add-btn2"
                                            role="button"
                                            tabIndex={0}
                                            data-bs-toggle="modal"
                                            data-bs-target="#inviteUser"
                                            onClick={() => {}}
                                            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') e.preventDefault(); }}
                                        >
                                            <svg style={{ width: 11, height: 11 }} viewBox="0 0 16 16" fill="none">
                                                <path d="M8 2V14M2 8H14" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
                                            </svg>
                                            {t('form.collectWorkspace.addRecipient', 'Add recipient')}
                                        </div>
                                        ) : null}
                                    </div>
                                    <div className="lp-section collect-ws-recent-section">
                                        <div className="lp-label">{t('form.collectWorkspace.recentDataCollections', 'Recent')}</div>
                                        {recentLoading && recentCollectionsVisible.length === 0 ? (
                                            <p className="collect-muted" style={{ fontSize: '0.78rem' }}>
                                                {t('words.loading')}
                                            </p>
                                        ) : null}
                                        {!recentLoading && recentCollectionsVisible.length === 0 ? (
                                            <p className="collect-muted" style={{ fontSize: '0.78rem' }}>
                                                {t('form.collectWorkspace.recentDataCollectionsEmpty', 'No collections yet.')}
                                            </p>
                                        ) : null}
                                        {recentCollectionsVisible.map((row) => {
                                            const active = row.id === collectionId;
                                            const canManage = canManageDataCollectionSummary(row);
                                            const showOwnerRowMenu = canManage;
                                            const showContributorRowMenu = Boolean(row.opened_as_contributor) && !canManage;
                                            const showRowMenu = showOwnerRowMenu || showContributorRowMenu;
                                            return (
                                                <div
                                                    key={row.id}
                                                    className={`collect-ws-recent-item ${active ? 'active' : ''}`}
                                                >
                                                    <button
                                                        type="button"
                                                        className="collect-ws-recent-item__nav"
                                                        onClick={() => goToRecentCollection(row)}
                                                    >
                                                        <span className="collect-ws-recent-item__name" title={row.name}>
                                                            {row.name}
                                                        </span>
                                                        <span className={`collect-ws-recent-item__meta ${collectCollectionBadgeClassName(row.status)}`}>
                                                            {collectCollectionBadgeLabel(t, row.status)}
                                                        </span>
                                                    </button>
                                                    {showRowMenu ? (
                                                        <div
                                                            className="collect-ws-recipient-menu"
                                                            onClick={(e) => e.stopPropagation()}
                                                            onKeyDown={(e) => e.stopPropagation()}
                                                        >
                                                            <div className="dropdown">
                                                                <button
                                                                    type="button"
                                                                    className="btn btn-link text-secondary text-decoration-none p-1 collect-ws-recipient-menu__toggle"
                                                                    data-bs-toggle="dropdown"
                                                                    data-bs-popper-config={BS_DROPDOWN_POPPER_FIXED}
                                                                    aria-expanded="false"
                                                                    aria-label={t(
                                                                        'form.collectWorkspace.recentCollectionRowMenu',
                                                                        'Collection actions',
                                                                    )}
                                                                    onClick={(e) => e.stopPropagation()}
                                                                >
                                                                    <i className="fa fa-ellipsis-vertical small" aria-hidden />
                                                                </button>
                                                                <ul className="dropdown-menu dropdown-menu-dark dropdown-menu-end small border border-secondary shadow-sm">
                                                                    {showOwnerRowMenu ? (
                                                                        <li>
                                                                            <button
                                                                                type="button"
                                                                                className="dropdown-item"
                                                                                onClick={() => handleRecentRename(row)}
                                                                            >
                                                                                {t('words.rename')}
                                                                            </button>
                                                                        </li>
                                                                    ) : null}
                                                                    {showOwnerRowMenu ? (
                                                                        <li>
                                                                            <button
                                                                                type="button"
                                                                                className="dropdown-item text-danger"
                                                                                onClick={() => setRecentDeleteRow(row)}
                                                                            >
                                                                                {t('words.delete')}
                                                                            </button>
                                                                        </li>
                                                                    ) : null}
                                                                    {showContributorRowMenu ? (
                                                                        <li>
                                                                            <button
                                                                                type="button"
                                                                                className="dropdown-item text-danger"
                                                                                onClick={() => setRecentWithdrawRow(row)}
                                                                            >
                                                                                {t('form.collectWorkspace.withdrawMyContribution', {
                                                                                    defaultValue: 'Remove my contribution',
                                                                                })}
                                                                            </button>
                                                                        </li>
                                                                    ) : null}
                                                                </ul>
                                                            </div>
                                                        </div>
                                                    ) : null}
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                                <SidebarUserProfileSection />
                            </div>

                            {/* ── CENTRE ── */}
                            <div className="centre">
                                <div className="c-topbar">
                                    <InterviewContextTopBar
                                        processTitle={collection?.name}
                                        ctaLabel={t('form.collectWorkspace.newDataCollection')}
                                        ctaOnClick={() => {
                                            navigate(Routes.DATA_COLLECT);
                                        }}
                                        titleAddon={
                                            collection?.status ? (
                                                <span className={collectCollectionBadgeClassName(collection.status)}>
                                                    {collectCollectionBadgeLabel(t, collection.status)}
                                                </span>
                                            ) : undefined
                                        }
                                    />
                                </div>

                                {loading && tab !== 'collect' ? (
                                    <div style={{ padding: '24px 28px' }}>
                                        <p className="collect-muted">{t('words.loading')}</p>
                                    </div>
                                ) : (
                                    <div className="collect-ws-main-pane">
                                        {tab === 'collect' && (
                                            <div className="collect-ws-thread-wrap">
                                                <div className="thread" ref={threadScrollRef}>
                                                    {scrollHeaderDisplay ? (
                                                        <div className="interview-scroll-date-sticky" aria-live="polite">
                                                            {scrollHeaderDisplay}
                                                        </div>
                                                    ) : null}
                                                    {loading && ownerMessages.length === 0 && !streamBusy ? (
                                                        <p className="collect-muted" style={{ padding: '8px 4px' }}>
                                                            {t('words.loading')}
                                                        </p>
                                                    ) : null}
                                                    {ownerMessages.map((m, idx) => {
                                                        const human = isOwnerUser(m.role);
                                                        const iso = m.created_at ?? m.createdAt;
                                                        const timeLabel = formatInterviewScrollHeader(iso);
                                                        const timeTitle = getChatTimestampHoverTitle(iso, chatReferenceNow);
                                                        const sender = human ? ownerDisplayName : getTenantName();
                                                        const isLast = lastOwnerMessage?.id === m.id;
                                                        const showHumanToolbar = human && collection?.status === 'draft' && editingMessageId !== m.id;
                                                        const humanEditEnabled = isLast && !streamBusy && !generatingGuide;
                                                        const humanDeleteEnabled = !streamBusy && !generatingGuide;
                                                        const humanEditHint = !isLast
                                                            ? t('form.collectWorkspace.editLastOnly')
                                                            : streamBusy || generatingGuide
                                                                ? t('form.collectWorkspace.waitForReply')
                                                                : undefined;
                                                        const humanDeleteHint =
                                                            streamBusy || generatingGuide ? t('form.collectWorkspace.waitForReply') : undefined;
                                                        const aiKey = String(m.id);
                                                        const isLastAiTurn =
                                                            m.role === 'assistant' &&
                                                            ownerMessages
                                                                .slice(idx + 1)
                                                                .every((n) => String(n?.role ?? '') !== 'assistant');

                                                        if (human) {
                                                            return (
                                                                <div key={m.id} data-msg-timestamp={iso || ''}>
                                                                    <div className="turn human human-turn">
                                                                    <div className="t-avatar t-human">{initialsFromName(ownerDisplayName)}</div>
                                                                    <div className="t-body">
                                                                        <div className="t-sender">
                                                                            <span className="s-human">
                                                                                {ownerDisplayName}
                                                                                <span className="t-time" title={timeTitle}>{timeLabel}</span>
                                                                            </span>
                                                                        </div>
                                                                        <div className="bubble-wrap">
                                                                            {showHumanToolbar ? (
                                                                                <div className="human-actions">
                                                                                    <button
                                                                                        type="button"
                                                                                        className="msg-btn"
                                                                                        title={humanEditEnabled ? t('words.edit') : humanEditHint}
                                                                                        disabled={!humanEditEnabled}
                                                                                        onClick={() => { if (!humanEditEnabled) return; beginEditMessage(m); }}
                                                                                    >
                                                                                        <svg viewBox="0 0 16 16" fill="none" aria-hidden>
                                                                                            <path d="M11 2L14 5L5 14H2V11L11 2Z" stroke="currentColor" strokeWidth="1.2" strokeLinejoin="round" />
                                                                                        </svg>
                                                                                    </button>
                                                                                    <button
                                                                                        type="button"
                                                                                        className="msg-btn danger"
                                                                                        title={humanDeleteEnabled ? t('words.delete') : humanDeleteHint}
                                                                                        disabled={!humanDeleteEnabled}
                                                                                        onClick={() => {
                                                                                            if (!humanDeleteEnabled) return;
                                                                                            setOwnerDeleteTargetId(m.id);
                                                                                        }}
                                                                                    >
                                                                                        <svg viewBox="0 0 16 16" fill="none" aria-hidden>
                                                                                            <path d="M3 5H13M6 5V3H10V5M5 5L5.5 13H10.5L11 5" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" />
                                                                                        </svg>
                                                                                    </button>
                                                                                </div>
                                                                            ) : null}
                                                                            <div className="bubble">
                                                                                {editingMessageId === m.id ? (
                                                                                    <>
                                                                                        <textarea className="field-input" rows={3} value={editDraft} onChange={(e) => setEditDraft(e.target.value)} />
                                                                                        <div style={{ marginTop: 8, display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                                                                                            <button type="button" className="action-btn" onClick={cancelEditMessage}>{t('form.collectWorkspace.editCancel')}</button>
                                                                                            <button type="button" className="action-btn primary" onClick={() => void saveEditMessage()}>{t('form.collectWorkspace.editSave')}</button>
                                                                                        </div>
                                                                                    </>
                                                                                ) : renderInlineBoldMd(String(m.content ?? ''))}
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                    </div>
                                                                </div>
                                                            );
                                                        }

                                                        return (
                                                            <div key={m.id} data-msg-timestamp={iso || ''}>
                                                                <div className="turn ai-turn">
                                                                <div className="t-avatar t-ai">{getTenantInitial()}</div>
                                                                <div className="t-body">
                                                                    <div className="t-sender">
                                                                        <span className="s-ai">
                                                                            {getTenantName()}
                                                                            <span className="t-time" title={timeTitle}>{formatInterviewScrollHeader(iso)}</span>
                                                                        </span>
                                                                    </div>
                                                                    <div className="bubble">{renderInlineBoldMd(String(m.content ?? ''))}</div>
                                                                    {renderAiHoverActions(aiKey, isLastAiTurn)}
                                                                </div>
                                                            </div>
                                                        </div>
                                                        );
                                                    })}
                                                    {showOwnerTypingBubble && (
                                                        <div className="turn ai-turn" aria-live="polite">
                                                            <div className="t-avatar t-ai">{getTenantInitial()}</div>
                                                            <div className="t-body">
                                                                <div className="t-sender s-ai">
                                                                    {getTenantName()}
                                                                    <span className="t-time" title={formatInterviewScrollHeader(new Date())}>
                                                                        {formatCollectWorkspaceRelativeLabel(new Date().toISOString(), chatReferenceNow).label}
                                                                    </span>
                                                                </div>
                                                                <div className="collect-typing-bubble">
                                                                    <span className="collect-typing-dot" />
                                                                    <span className="collect-typing-dot" />
                                                                    <span className="collect-typing-dot" />
                                                                </div>
                                                            </div>
                                                        </div>
                                                    )}
                                                    {generatingGuide && (
                                                        <div className="collect-gen-status">
                                                            <span className="collect-gen-dot" aria-hidden />
                                                            {t('form.collectWorkspace.generatingGuide')}
                                                        </div>
                                                    )}
                                                    {showCollectContextCta && (
                                                    <div className="collect-context-cta" role="region" aria-label={t('views.processInterview.contextChoiceAria')}>
                                                        <div className="collect-context-cta-row">
                                                            <button type="button" className="action-btn primary" disabled={contextFileBusy} onClick={() => setIsAnalysisFileModalOpen(true)}>
                                                                {contextFileBusy ? t('form.collectWorkspace.contextFileWorking') : t('form.collectWorkspace.addContextButton')}
                                                            </button>
                                                            <button type="button" className="action-btn" disabled={contextFileBusy} onClick={onCollectContextSkip}>
                                                                {t('views.processInterview.skipForNow')}
                                                            </button>
                                                        </div>
                                                    </div>
                                                )}
                                                {showGuideReadyCta && (
                                                    <div className="collect-context-cta" role="region">
                                                        <p className="collect-context-cta-hint collect-muted">{t('form.collectWorkspace.checklist.guide.detailReady')}</p>
                                                        {!readyForGuide ? (
                                                            <p className="collect-context-cta-hint collect-muted" style={{ marginTop: 6 }}>
                                                                {t(
                                                                    'form.collectWorkspace.setupNotReadyHint',
                                                                    'Answer the remaining setup questions (objective, context, and roles) before you can approve.',
                                                                )}
                                                            </p>
                                                        ) : null}
                                                        <div className="collect-context-cta-row">
                                                            <button type="button" className="action-btn primary" onClick={() => { setTab('guide'); void reloadGuide(); }}>
                                                                {t('form.collectWorkspace.guideReviewTitle')}
                                                            </button>
                                                            <button
                                                                type="button"
                                                                className="action-btn"
                                                                disabled={!readyForGuide || approvingGuide}
                                                                title={
                                                                    !readyForGuide
                                                                        ? t(
                                                                              'form.collectWorkspace.setupNotReadyShort',
                                                                              'Complete setup in Collect first.',
                                                                          )
                                                                        : undefined
                                                                }
                                                                onClick={() => void onApproveGuide()}
                                                                aria-busy={approvingGuide}
                                                            >
                                                                {approvingGuide
                                                                    ? t(
                                                                          'form.collectWorkspace.approvingGuide',
                                                                          'Approving…',
                                                                      )
                                                                    : t('form.collectWorkspace.approveGuide')}
                                                            </button>
                                                        </div>
                                                    </div>
                                                )}
                                                {showApproveRetryCta && (
                                                    <div className="collect-context-cta" role="region">
                                                        <p className="collect-context-cta-hint collect-muted">
                                                            {t('form.collectWorkspace.approveRetryHint')}
                                                        </p>
                                                        <div className="collect-context-cta-row">
                                                            <button
                                                                type="button"
                                                                className="action-btn"
                                                                onClick={() => void onApproveGuide()}
                                                                disabled={approvingGuide}
                                                                aria-busy={approvingGuide}
                                                            >
                                                                {approvingGuide
                                                                    ? t('form.collectWorkspace.approvingGuide', 'Approving…')
                                                                    : t('form.collectWorkspace.approveGuide')}
                                                            </button>
                                                        </div>
                                                    </div>
                                                )}
                                                </div>
                                                <div
                                                    className={`input-bar${setupCtaBlocksOwnerInput ? ' input-bar--setup-cta-locked' : ''}`}
                                                >
                                                    <VoiceRealtimeBar
                                                        ownerCollectionId={collectionId}
                                                        accessToken={access_token}
                                                        disabled={
                                                            collection?.status !== 'draft' ||
                                                            streamBusy ||
                                                            generatingGuide ||
                                                            setupCtaBlocksOwnerInput
                                                        }
                                                        t={t}
                                                        pendingQuestionText={pendingOwnerVoiceQuestion}
                                                        textDraft={setupInput}
                                                        onVoiceSessionChange={setVoiceSessionActive}
                                                        onPersistSuccess={() => void load({ soft: true })}
                                                    />
                                                    <div className="input-inner">
                                                        <textarea
                                                            className="input-field"
                                                            rows={2}
                                                            value={setupInput}
                                                            onChange={(e) => setSetupInput(e.target.value)}
                                                            placeholder={t('form.collectWorkspace.typeAnswer')}
                                                            title={setupCtaInputLockedHint}
                                                            disabled={
                                                                collection?.status !== 'draft' ||
                                                                streamBusy ||
                                                                generatingGuide ||
                                                                voiceSessionActive ||
                                                                setupCtaBlocksOwnerInput
                                                            }
                                                            onKeyDown={(e) => {
                                                                if (e.key === 'Enter' && !e.shiftKey) {
                                                                    e.preventDefault();
                                                                    postSetup();
                                                                }
                                                            }}
                                                        />
                                                        <div className="input-actions">
                                                            <button
                                                                type="button"
                                                                className="input-btn voice-btn"
                                                                title={t('features.swimlane.addDocument')}
                                                                disabled={
                                                                    collection?.status !== 'draft' ||
                                                                    streamBusy ||
                                                                    generatingGuide ||
                                                                    voiceSessionActive ||
                                                                    setupCtaBlocksOwnerInput
                                                                }
                                                                onClick={() => setIsAnalysisFileModalOpen(true)}
                                                            >
                                                                <svg viewBox="0 0 16 16" fill="none" aria-hidden><path d="M8 3V13M3 8H13" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" /></svg>
                                                            </button>
                                                            <button
                                                                type="button"
                                                                className="input-btn send-btn"
                                                                onClick={postSetup}
                                                                disabled={
                                                                    collection?.status !== 'draft' ||
                                                                    streamBusy ||
                                                                    generatingGuide ||
                                                                    voiceSessionActive ||
                                                                    setupCtaBlocksOwnerInput
                                                                }
                                                                aria-label={t('words.send')}
                                                                title={t('words.send')}
                                                            >
                                                                <svg viewBox="0 0 16 16" fill="none" aria-hidden><path d="M13 8L3 3L5.5 8L3 13L13 8Z" stroke="currentColor" strokeWidth="1.2" strokeLinejoin="round" /></svg>
                                                            </button>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        )}
                                        {tab === 'guide' && (
                                            <div className="collect-ws-secondary collect-ws-guide-owner" style={{ padding: '16px 20px 24px' }}>
                                                {guideAiAdaptBannerNote()}
                                                {guideActionToolbar('collect-guide-toolbar-row--top')}
                                                <h2 className="collect-panel-title collect-ws-guide-owner__title">{t('form.collectWorkspace.guideReviewTitle')}</h2>
                                                <div
                                                    className={`collect-ws-guide-body${generatingGuide ? ' is-generating' : ''}`}
                                                    aria-busy={generatingGuide}
                                                >
                                                    {generatingGuide ? (
                                                        <div
                                                            className="collect-gen-status collect-gen-status--panel"
                                                            role="status"
                                                            aria-live="polite"
                                                        >
                                                            <span className="collect-gen-dot" aria-hidden />
                                                            {t('form.collectWorkspace.generatingGuide')}
                                                        </div>
                                                    ) : null}
                                                    <CollectGuideReviewPanel
                                                        collectionId={collectionId}
                                                        accessToken={access_token}
                                                        guide={guide}
                                                        canEdit={collection?.status === 'draft' && !generatingGuide}
                                                        onGuideUpdated={(g) => setGuide(g)}
                                                        onError={(msg) => setError(msg)}
                                                    />
                                                </div>
                                                {guideActionToolbar('collect-guide-toolbar-row--bottom')}
                                            </div>
                                        )}
                                        {tab === 'monitor' && (
                                            <div className="collect-ws-thread-wrap monitor-view">
                                                <div className="monitor-header" style={{ padding: '12px 28px', borderBottom: '1px solid rgba(255,255,255,0.05)', backgroundColor: 'rgba(255,255,255,0.02)' }}>
                                                    <span className="collect-muted" style={{ fontSize: '0.8rem' }}>{t('form.collectWorkspace.transcriptTitle', 'Transcript')} — </span>
                                                    <span style={{ fontWeight: 600, color: '#fff' }}>
                                                        {(() => {
                                                            const selHdr = selectedContrib
                                                                ? contributors.find((x) => x.id === selectedContrib)
                                                                : undefined;
                                                            return selHdr
                                                                ? contributorDisplayName(selHdr)
                                                                : t('form.collectWorkspace.selectContributor', 'Select a contributor');
                                                        })()}
                                                    </span>
                                                </div>
                                                <div className="thread" style={{ flexGrow: 1, overflowY: 'auto' }}>
                                                    {!selectedContrib && (
                                                        <div style={{ padding: 40, textAlign: 'center' }}>
                                                            <p className="collect-muted">{t('form.collectWorkspace.selectContributor', 'Select a contributor to view their transcript')}</p>
                                                        </div>
                                                    )}
                                                    {selectedContrib && thread.length === 0 && (
                                                        <div style={{ padding: 40, textAlign: 'center' }}>
                                                            <p className="collect-muted">{t('form.collectWorkspace.noMessagesYet', 'No messages yet for this contributor')}</p>
                                                        </div>
                                                    )}
                                                    {selectedContrib && thread.map((m) => {
                                                        const human = isOwnerUser(m.role);
                                                        const sel = contributors.find((x) => x.id === selectedContrib);
                                                        const senderName = human
                                                            ? (sel ? contributorDisplayName(sel) : 'Contributor')
                                                            : getTenantName();
                                                        const iso = m.created_at ?? m.createdAt;
                                                        const timeLabel = formatInterviewScrollHeader(iso);
                                                        const timeTitle = getChatTimestampHoverTitle(iso, chatReferenceNow);
                                                        return (
                                                            <div key={m.id} className={`turn ${human ? 'human human-turn' : 'ai-turn'}`}>
                                                                <div className={`t-avatar ${human ? 't-human' : 't-ai'}`}>{initialsFromName(senderName)}</div>
                                                                <div className="t-body">
                                                                    <div className="t-sender">
                                                                        <span className={human ? 's-human' : 's-ai'}>
                                                                            {senderName}
                                                                            {timeLabel ? (
                                                                                <span className="t-time" title={timeTitle}>
                                                                                    {timeLabel}
                                                                                </span>
                                                                            ) : null}
                                                                        </span>
                                                                    </div>
                                                                    <div className="bubble">{renderInlineBoldMd(String(m.content ?? ''))}</div>
                                                                    {!human && (() => {
                                                                        const aiIdx = monitorAssistantMessages.findIndex(
                                                                            (x) => x.id === m.id,
                                                                        );
                                                                        const intent =
                                                                            aiIdx >= 0 ? monitorGuideIntents[aiIdx] : null;
                                                                        if (
                                                                            !intent ||
                                                                            String(m.content ?? '').trim() === intent.trim()
                                                                        ) {
                                                                            return null;
                                                                        }
                                                                        return (
                                                                            <div
                                                                                className="collect-monitor-guide-intent collect-muted"
                                                                                style={{ fontSize: '0.72rem', marginTop: 6 }}
                                                                                title={intent}
                                                                            >
                                                                                {t(
                                                                                    'form.collectWorkspace.monitorGuideIntentLabel',
                                                                                    'Script intent',
                                                                                )}
                                                                                : {intent}
                                                                            </div>
                                                                        );
                                                                    })()}
                                                                </div>
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>

                            {/* ── RIGHT PANEL ── */}
                            <div className="right-panel">
                                <div className="rp-scroll">
                                    <div className="rp-sec">
                                        <div className="rp-title-row">
                                            <div className="rp-title">
                                                {useFullLaunchChecklist
                                                    ? t('words.progress', 'Progress')
                                                    : t('form.collectWorkspace.completionTitle', 'Completion')}
                                            </div>
                                            <div className="prog-pct">
                                                {useFullLaunchChecklist
                                                    ? launchChecklistProgressPct
                                                    : interviewStats.total > 0
                                                        ? Math.round((interviewStats.completed / interviewStats.total) * 100)
                                                        : 0}
                                                %
                                            </div>
                                        </div>
                                        <div className="prog-bar-row">
                                            <div className="prog-track">
                                                <div
                                                    className="prog-fill"
                                                    style={{
                                                        width: `${
                                                            useFullLaunchChecklist
                                                                ? launchChecklistProgressPct
                                                                : interviewStats.total > 0
                                                                    ? Math.round((interviewStats.completed / interviewStats.total) * 100)
                                                                    : 0
                                                        }%`,
                                                    }}
                                                />
                                            </div>
                                        </div>
                                        {useFullLaunchChecklist ? (
                                            checklistSteps.map((s, index) => (
                                                <div key={s.key}>
                                                    {s.key === 'interviews' ? (
                                                        <div className="p-step" style={{ cursor: 'default' }}>
                                                            <div
                                                                className={`step-dot ${s.done ? 'sd-done' : s.active ? 'sd-active' : 'sd-pending'}`}
                                                                aria-hidden
                                                            >
                                                                {s.done && <span className="step-check">✓</span>}
                                                            </div>
                                                            <div className="step-info">
                                                                <div className={`step-name ${!s.done && !s.active ? 'muted' : ''}`}>{s.label}</div>
                                                                <div className="step-detail">{s.detail}</div>
                                                            </div>
                                                        </div>
                                                    ) : (
                                                        <div
                                                            className="p-step"
                                                            role="button"
                                                            tabIndex={0}
                                                            onClick={() => handleChecklistClick(s.key)}
                                                            onKeyDown={(e) => {
                                                                if (e.key === 'Enter' || e.key === ' ') {
                                                                    e.preventDefault();
                                                                    handleChecklistClick(s.key);
                                                                }
                                                            }}
                                                        >
                                                            <div
                                                                className={`step-dot ${s.done ? 'sd-done' : s.active ? 'sd-active' : 'sd-pending'}`}
                                                                aria-hidden
                                                            >
                                                                {s.done && <span className="step-check">✓</span>}
                                                            </div>
                                                            <div className="step-info">
                                                                <div className={`step-name ${!s.done && !s.active ? 'muted' : ''}`}>{s.label}</div>
                                                                <div className="step-detail">{s.detail}</div>
                                                            </div>
                                                        </div>
                                                    )}
                                                    {index < checklistSteps.length - 1 && <div className="step-line" />}
                                                </div>
                                            ))
                                        ) : (
                                            <div style={{ marginTop: 12 }}>
                                                <div className="p-step" style={{ cursor: 'default' }}>
                                                    <div
                                                        className={`step-dot ${
                                                            interviewStats.total > 0 && interviewStats.completed === interviewStats.total
                                                                ? 'sd-done'
                                                                : interviewStats.total > 0
                                                                    ? 'sd-active'
                                                                    : 'sd-pending'
                                                        }`}
                                                        aria-hidden
                                                    >
                                                        {interviewStats.total > 0 && interviewStats.completed === interviewStats.total ? (
                                                            <span className="step-check">✓</span>
                                                        ) : null}
                                                    </div>
                                                    <div className="step-info">
                                                        <div className={`step-name ${interviewStats.total === 0 ? 'muted' : ''}`}>
                                                            {t('form.collectWorkspace.checklist.interviews.label')}
                                                        </div>
                                                        <div className="step-detail">
                                                            {t('form.collectWorkspace.checklist.interviews.detailProgress', {
                                                                completed: interviewStats.completed,
                                                                total: interviewStats.total,
                                                            })}
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                    <div className="rp-sec rp-sec--quick collect-ws-quick-actions">
                                        <div className="rp-title">{t('form.collectWorkspace.quickActionsTitle')}</div>
                                        <Link to={Routes.DATA_COLLECT} className="qa-row collect-ws-qa-primary">
                                            <svg style={{ width: 12, height: 12 }} viewBox="0 0 16 16" fill="none" aria-hidden>
                                                <path d="M8 2V14M2 8H14" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
                                            </svg>
                                            {t('form.collectWorkspace.newDataCollection')}
                                        </Link>
                                        <button type="button" className="qa-row sec" onClick={openViewAsRecipient}>
                                            <svg style={{ width: 12, height: 12 }} viewBox="0 0 16 16" fill="none" aria-hidden>
                                                <path
                                                    d="M6 3H4a1 1 0 0 0-1 1v8a1 1 0 0 0 1 1h8a1 1 0 0 0 1-1V9M10 1h4v4M14 1L7.5 7.5"
                                                    stroke="currentColor"
                                                    strokeWidth="1.2"
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                />
                                            </svg>
                                            {t('form.collectWorkspace.viewAsRecipient', 'View as recipient')}
                                        </button>
                                        {showApproveRetryCta ? (
                                            <button
                                                type="button"
                                                className="qa-row collect-ws-qa-primary"
                                                onClick={() => void onApproveGuide()}
                                            >
                                                <svg style={{ width: 12, height: 12 }} viewBox="0 0 16 16" fill="none" aria-hidden>
                                                    <path
                                                        d="M3 8.5L6.5 12L13 4"
                                                        stroke="currentColor"
                                                        strokeWidth="1.4"
                                                        strokeLinecap="round"
                                                        strokeLinejoin="round"
                                                    />
                                                </svg>
                                                {t('form.collectWorkspace.approveGuide')}
                                            </button>
                                        ) : null}
                                        <button
                                            type="button"
                                            className="qa-row sec"
                                            onClick={() => {
                                                setError(null);
                                                if (!guideReady && !guideApproved && !generatingGuide) {
                                                    setError(
                                                        t(
                                                            'form.collectWorkspace.viewInterviewScriptEmpty',
                                                            'Generate or approve the interview script first.',
                                                        ),
                                                    );
                                                    return;
                                                }
                                                setTab('guide');
                                                void reloadGuide();
                                            }}
                                        >
                                            <svg style={{ width: 12, height: 12 }} viewBox="0 0 16 16" fill="none" aria-hidden>
                                                <path
                                                    d="M4 2.5h8a1 1 0 0 1 1 1v9l-2.5-1.5H4a1 1 0 0 1-1-1v-6.5a1 1 0 0 1 1-1Z"
                                                    stroke="currentColor"
                                                    strokeWidth="1.2"
                                                    strokeLinejoin="round"
                                                />
                                                <path d="M5.5 6h5M5.5 8.5h3" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" />
                                            </svg>
                                            {t('form.collectWorkspace.viewInterviewScript', 'View interview script')}
                                        </button>
                                        <Link className="qa-row sec" to={`/process/data-collect/${collectionId}/synthesis`}>
                                            <svg style={{ width: 12, height: 12 }} viewBox="0 0 16 16" fill="none" aria-hidden>
                                                <path d="M3 12L8 4L13 12" stroke="currentColor" strokeWidth="1.2" strokeLinejoin="round" />
                                                <path d="M5 10H11" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" />
                                            </svg>
                                            {t('form.collectWorkspace.openSynthesis', 'Open synthesis')}
                                        </Link>
                                        <button
                                            type="button"
                                            className="qa-row sec"
                                            onClick={() => {
                                                setError(null);
                                                if (!guideApproved || !hasGuideContent(guide)) {
                                                    setError(
                                                        t(
                                                            'form.collectWorkspace.exportGuideNeedApproved',
                                                            'Approve the interview script before exporting it as PDF.',
                                                        ),
                                                    );
                                                    return;
                                                }
                                                const approvedIso =
                                                    typeof collection?.guide_approved_at === 'string'
                                                        ? collection.guide_approved_at
                                                        : '';
                                                downloadCollectGuidePdf({
                                                    collectionName: String(collection?.name ?? ''),
                                                    sections: iterGuideSubSections(guide),
                                                    strings: {
                                                        documentTitle: t(
                                                            'form.collectWorkspace.exportGuidePdfTitle',
                                                            'Approved interview script',
                                                        ),
                                                        approvedStamp: approvedIso
                                                            ? t('form.collectWorkspace.exportGuideApprovedStamp', {
                                                                  date: formatDateTime(parseServerInstant(approvedIso)),
                                                                  defaultValue: 'Approved on {{date}}',
                                                              })
                                                            : undefined,
                                                        sectionObjective: t(
                                                            'form.collectWorkspace.guideSectionObjective',
                                                            'Section objective',
                                                        ),
                                                        questionPrefix: t(
                                                            'form.collectWorkspace.exportGuideQuestionPrefix',
                                                            'Q',
                                                        ),
                                                    },
                                                });
                                            }}
                                        >
                                            <svg style={{ width: 12, height: 12 }} viewBox="0 0 16 16" fill="none" aria-hidden>
                                                <path d="M3 13V8L6 10L10 4L13 7V13H3Z" stroke="currentColor" strokeWidth="1.2" strokeLinejoin="round" />
                                                <path d="M3 8V3H8" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" />
                                            </svg>
                                            {t('form.collectWorkspace.exportTranscripts', 'Export interview script')}
                                        </button>
                                        <button
                                            type="button"
                                            className="qa-row sec"
                                            data-bs-toggle="modal"
                                            data-bs-target="#collectWorkspaceSettings"
                                        >
                                            <svg style={{ width: 12, height: 12 }} viewBox="0 0 16 16" fill="none" aria-hidden>
                                                <circle cx="8" cy="8" r="2" stroke="currentColor" strokeWidth="1.2" />
                                                <path d="M8 2V3.5M8 12.5V14M14 8H12.5M3.5 8H2M12.2 3.8L11.1 4.9M4.9 11.1L3.8 12.2M12.2 12.2L11.1 11.1M4.9 4.9L3.8 3.8" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" />
                                            </svg>
                                            {t('form.collectWorkspace.openSettings', 'Open settings')}
                                        </button>
                                    </div>
                                </div>

                                <div className="rp-announce-wrap">
                                    <ScrollingNotification
                                        className="rp-scrolling-notification"
                                        messages={railNotificationMessages}
                                        onDismiss={(messageId) => { console.log('Dismissed notification:', messageId); }}
                                        onClick={(message) => { console.log('Clicked notification:', message); }}
                                    />
                                </div>
                            </div>

                        </div>{/* /.shell */}
                    </div>{/* /.interview-shell */}

                    {ownerDeleteTargetId ? (
                        <GenericModal
                            title={t('words.delete')}
                            actionButton={t('words.delete')}
                            sentence={t('form.collectWorkspace.ownerSetupDeleteConfirm')}
                            processId={ownerDeleteTargetId}
                            modalDomId={`collect-owner-delete-${ownerDeleteTargetId}`}
                            autoShow
                            onHidden={() => setOwnerDeleteTargetId(null)}
                            action={async () => {
                                const ok = await deleteOwnerSetupFromMessage(ownerDeleteTargetId);
                                if (ok) setOwnerDeleteTargetId(null);
                                return ok;
                            }}
                        />
                    ) : null}

                    {recentRenameRow ? (
                        <RenameDataCollectionModal
                            collectionId={recentRenameRow.id}
                            initialName={recentRenameRow.name}
                            modalDomId={`famla-dc-rename-${recentRenameRow.id}`}
                            autoShow
                            onHidden={() => setRecentRenameRow(null)}
                            onSave={executeRecentRename}
                        />
                    ) : null}

                    {recentDeleteRow ? (
                        <GenericModal
                            title={t('words.delete')}
                            actionButton={t('words.delete')}
                            sentence={
                                String(recentDeleteRow.status).toLowerCase() === 'draft'
                                    ? t('form.collectWorkspace.deleteDataCollectionConfirm', {
                                          defaultValue: 'Delete this draft?',
                                      })
                                    : t('form.collectWorkspace.deleteDataCollectionConfirmActive', {
                                          defaultValue:
                                              'Delete this collection and all responses? This cannot be undone.',
                                      })
                            }
                            processId={recentDeleteRow.id}
                            modalDomId={`famla-dc-delete-recent-${recentDeleteRow.id}`}
                            autoShow
                            onHidden={() => setRecentDeleteRow(null)}
                            action={() => executeRecentDelete()}
                        />
                    ) : null}

                    {recentWithdrawRow ? (
                        <GenericModal
                            title={t('form.collectWorkspace.withdrawMyContribution', {
                                defaultValue: 'Remove my contribution',
                            })}
                            actionButton={t('form.collectWorkspace.withdrawMyContribution', {
                                defaultValue: 'Remove my contribution',
                            })}
                            sentence={t('form.collectWorkspace.withdrawMyContributionConfirm', {
                                defaultValue:
                                    'Remove your contribution from this collection? Your responses and messages will be deleted.',
                            })}
                            processId={recentWithdrawRow.id}
                            modalDomId={`famla-dc-withdraw-recent-${recentWithdrawRow.id}`}
                            autoShow
                            onHidden={() => setRecentWithdrawRow(null)}
                            action={() => executeRecentWithdraw()}
                        />
                    ) : null}

                    {recipientDeleteTarget ? (
                        <GenericModal
                            title={t('words.delete')}
                            actionButton={t('words.delete')}
                            sentence={t('form.collectWorkspace.deleteContributorConfirm', {
                                name: contributorDisplayName(recipientDeleteTarget) || '—',
                            })}
                            processId={recipientDeleteTarget.id}
                            modalDomId={`famla-dc-delete-recipient-${recipientDeleteTarget.id}`}
                            autoShow
                            onHidden={() => setRecipientDeleteTarget(null)}
                            action={() => executeDeleteRecipient()}
                        />
                    ) : null}

                    {recipientReplaceTarget ? (
                        <GenericModal
                            title={t('words.replace', 'Replace')}
                            actionButton={t('words.replace', 'Replace')}
                            sentence={t('form.collectWorkspace.replaceContributorConfirm', {
                                name: contributorDisplayName(recipientReplaceTarget) || '—',
                            })}
                            processId={recipientReplaceTarget.id}
                            modalDomId={`famla-dc-replace-recipient-${recipientReplaceTarget.id}`}
                            autoShow
                            onHidden={() => setRecipientReplaceTarget(null)}
                            action={() => executeReplaceRecipient()}
                        />
                    ) : null}

                    <InviteUser
                        processId={collectionId!}
                        closeRef={inviteCloseRef}
                        interviewwId={collectionId}
                        invited={undefined}
                        availableRoles={collectInviteAvailableRoles}
                        roleSelectOnly
                        responderLinkToken={collection?.responder_link_token}
                        onCustomSubmit={(data) => submitInviteContributors(data)}
                    />
                    <AnalysisFileModal
                        isOpen={isAnalysisFileModalOpen}
                        onClose={() => setIsAnalysisFileModalOpen(false)}
                        onConfirm={handleAnalysisFileConfirm}
                        mode="collectContext"
                        selectedFile={null}
                        selectedMapIds={selectedMapIds}
                        swimlanes={[]}
                        fileError={null}
                        handleFileSelect={() => { }}
                        handleMapSelect={() => { }}
                        clearSelection={() => { }}
                    />
                    <CollectWorkspaceSettingsModal
                        collectionId={collectionId}
                        accessToken={access_token}
                        collection={collection}
                        t={t}
                        onSaved={() => load({ soft: true })}
                    />
                </div>
            </>
        </AppLayout>
    );
};

export default CollectDataWorkspaceView;

